// @ts-nocheck
import '../shared/shell'
import { maskedProfileEmail, playerAvatarPresentation, profileBackgroundPresentation, safeAvatarColor } from '../shared/profilePresentation'
import { isRestorableLocalEntry, shouldUseLocalDemoData } from './startup'

const state = {
  contest: null,
  leaderboard: [],
  leaderboardContests: [],
  leaderboardContest: null,
  leaderboardContestID: 0,
  archive: [],
  awardRecords: [],
  awardPage: 1,
  awardPageSize: 3,
  leaderboardPage: 1,
  leaderboardPageSize: 20,
  archivePage: 1,
  archivePageSize: 3,
  localPage: 1,
  localPageSize: 18,
  localContestFilter: "all",
  localModelFilter: "all",
  localSelectedIDs: new Set(),
  submissionQuota: null,
  submissionId: "",
  imageDataURL: "",
  judgeModels: [],
  judgeModelsKey: "",
  imageProvider: "openai",
  composerModelMenuLevel: "root",
  composerModelMenuOpen: false,
  modelAutoLoadTimers: { image: 0, judge: 0 },
  modelAutoLoadSeq: { image: 0, judge: 0 },
  modelAutoLoading: { image: false, judge: false },
  winnerReviewChecked: false,
  shareCard: null,
  profile: null,
  profileKey: "",
  profileAutoAttempted: false,
  profileLoading: false,
  podiumRenderToken: 0,
  podiumAnimatedContestIDs: new Set(),
  timer: null,
  heroBackgroundTimer: null,
  heroTitleFitFrame: null,
  saveStatusTimer: null,
  demoData: false,
};

const imageViewer = {
  scale: 1,
  x: 0,
  y: 0,
  dragging: false,
  pointerId: null,
  startX: 0,
  startY: 0,
  originX: 0,
  originY: 0,
};

const $ = (selector) => document.querySelector(selector);

function prefersReducedMotion() {
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

function navigationScrollBehavior() {
  return prefersReducedMotion() ? 'auto' : 'smooth';
}

function confirmAction({ title, message, confirmLabel = "确认", danger = true }) {
  const dialog = $("#confirmDialog");
  $("#confirmTitle").textContent = title;
  $("#confirmMessage").textContent = message;
  const action = $("#confirmActionButton");
  action.textContent = confirmLabel;
  action.classList.toggle("danger", danger);
  dialog.returnValue = "";
  if (dialog.open) dialog.close("cancel");
  dialog.showModal();
  return new Promise((resolve) => dialog.addEventListener("close", () => resolve(dialog.returnValue === "confirm"), { once: true }));
}

function setSaveStatus(message = "", hideAfter = 0) {
  const node = $("#saveStatus");
  if (!node) return;
  clearTimeout(state.saveStatusTimer);
  state.saveStatusTimer = null;
  node.textContent = message;
  node.hidden = !message;
  if (message && hideAfter > 0) {
    state.saveStatusTimer = setTimeout(() => {
      node.textContent = "";
      node.hidden = true;
      state.saveStatusTimer = null;
    }, hideAfter);
  }
}

function readBrowserStorage(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeBrowserStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Local browser storage is optional; remote contest loading must keep working.
  }
}

function runOptionalBrowserTask(task, errorMessage, timeoutMs = 1600) {
  const timeout = new Promise((_, reject) => {
    setTimeout(() => reject(new Error("BROWSER_STORAGE_TIMEOUT")), timeoutMs);
  });
  Promise.race([Promise.resolve().then(task), timeout]).catch(() => {
    if (errorMessage) toast(errorMessage, "error");
  });
}

function scheduleAfterInitialPaint(task, { timeoutMs = 2400, fallbackDelayMs = 600 } = {}) {
  const scheduleIdle = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => void task(), { timeout: timeoutMs });
      return;
    }
    window.setTimeout(() => void task(), fallbackDelayMs);
  };
  const afterPaint = () => requestAnimationFrame(() => requestAnimationFrame(scheduleIdle));
  if (document.readyState === "complete") afterPaint();
  else window.addEventListener("load", afterPaint, { once: true });
}

let workbenchPrefetchStarted = false;
let workbenchServiceWorkerStarted = false;

async function refreshWorkbenchServiceWorker() {
  if (workbenchServiceWorkerStarted || !('serviceWorker' in navigator)) return;
  workbenchServiceWorkerStarted = true;
  try {
    const registration = await navigator.serviceWorker.register('/ui/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    await registration.update();
  } catch {
    workbenchServiceWorkerStarted = false;
  }
}

async function prefetchWorkbench() {
  if (workbenchPrefetchStarted) return;
  workbenchPrefetchStarted = true;
  void refreshWorkbenchServiceWorker();
  try {
    const response = await fetch('/studio/', { credentials: 'same-origin' });
    if (!response.ok) return;
    const documentCopy = new DOMParser().parseFromString(await response.text(), 'text/html');
    const resources = [...documentCopy.querySelectorAll('script[src], link[rel="stylesheet"][href]')];
    resources.forEach((resource) => {
      const value = resource.getAttribute(resource.tagName === 'SCRIPT' ? 'src' : 'href');
      if (!value) return;
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !url.pathname.startsWith('/ui/assets/')) return;
      if (document.head.querySelector(`link[data-workbench-prefetch="${CSS.escape(url.href)}"]`)) return;
      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = url.href;
      hint.as = resource.tagName === 'SCRIPT' ? 'script' : 'style';
      hint.dataset.workbenchPrefetch = url.href;
      document.head.append(hint);
    });
  } catch {
    workbenchPrefetchStarted = false;
  }
}

function openLocalImageDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("c2p-daily-frame", 4);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("entries")) {
        request.result.createObjectStore("entries", { keyPath: "contest_id" });
      }
      if (!request.result.objectStoreNames.contains("credentials")) {
        request.result.createObjectStore("credentials", { keyPath: "id" });
      }
      let entries;
      if (!request.result.objectStoreNames.contains("submission_entries")) {
        entries = request.result.createObjectStore("submission_entries", { keyPath: "submission_id" });
        entries.createIndex("contest_id", "contest_id", { unique: false });
        if (request.result.objectStoreNames.contains("entries")) {
          const legacy = request.transaction.objectStore("entries").openCursor();
          legacy.onsuccess = () => {
            const cursor = legacy.result;
            if (!cursor) return;
            if (cursor.value?.submission_id) entries.put({ ...cursor.value, contest: null });
            cursor.continue();
          };
        }
      } else {
        entries = request.transaction.objectStore("submission_entries");
      }
      if (!entries.indexNames.contains("saved_at")) entries.createIndex("saved_at", "saved_at", { unique: false });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveLocalCredential(id, key) {
  const database = await openLocalImageDB();
  await new Promise((resolve, reject) => {
    const transaction = database.transaction("credentials", "readwrite");
    transaction.objectStore("credentials").put({ id, key, saved_at: new Date().toISOString() });
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

async function readLocalCredential(id) {
  const database = await openLocalImageDB();
  const saved = await new Promise((resolve, reject) => {
    const request = database.transaction("credentials", "readonly").objectStore("credentials").get(id);
    request.onsuccess = () => resolve(request.result?.key || "");
    request.onerror = () => reject(request.error);
  });
  database.close();
  return saved;
}

function modelPreferenceStorageKey(purpose) {
  return purpose === "image" ? `arena-image-model:${state.imageProvider}` : "arena-judge-model";
}

function readPreferredModel(purpose) {
  return readBrowserStorage(modelPreferenceStorageKey(purpose));
}

function writePreferredModel(purpose, model) {
  const value = String(model || "").trim();
  if (!value) return;
  writeBrowserStorage(modelPreferenceStorageKey(purpose), value);
}

function preferredModelForSelect(selectSelector) {
  if (selectSelector === "#imageModel") return readPreferredModel("image");
  if (selectSelector === "#judgeModel" || selectSelector === "#retryJudgeModel") return readPreferredModel("judge");
  return "";
}

function keyLooksReady(key) {
  return String(key || "").trim().length >= 8;
}

async function restoreSavedKeys() {
  const [imageKey, judgeKey] = await Promise.all([
    readLocalCredential(`image:${state.imageProvider}`),
    readLocalCredential("judge"),
  ]);
  $("#imageKey").value = imageKey;
  $("#judgeKey").value = judgeKey;
  updateComposerModelPicker();
}

async function restoreSavedImageKey() {
  $("#imageKey").value = await readLocalCredential(`image:${state.imageProvider}`);
  updateComposerModelPicker();
}

async function restoreSavedKeysAndModels({ silent = true } = {}) {
  await restoreSavedKeys();
  await Promise.all([
    autoLoadModelsForPurpose("image", { silent, reason: "restore" }),
    autoLoadModelsForPurpose("judge", { silent, reason: "restore" }),
  ]);
  updateComposerModelPicker();
  syncComposerControls();
}

function scheduleAutoLoadModels(purpose, { delay = 650 } = {}) {
  const kind = purpose === "judge" ? "judge" : "image";
  window.clearTimeout(state.modelAutoLoadTimers[kind]);
  state.modelAutoLoadTimers[kind] = window.setTimeout(() => {
    autoLoadModelsForPurpose(kind, { silent: true, reason: "input" }).catch(() => {});
  }, delay);
}

async function autoLoadModelsForPurpose(purpose, { silent = true, reason = "auto" } = {}) {
  const kind = purpose === "judge" ? "judge" : "image";
  const keySelector = kind === "image" ? "#imageKey" : "#judgeKey";
  const selectSelector = kind === "image" ? "#imageModel" : "#judgeModel";
  const buttonSelector = kind === "image" ? "#loadImageModels" : "#loadJudgeModels";
  const key = $(keySelector)?.value.trim() || "";
  if (!keyLooksReady(key)) {
    if (!key) resetModelSelect(selectSelector, kind === "image" ? "先读取生图模型" : "先读取评分模型");
    updateComposerModelPicker();
    return false;
  }
  if (state.modelAutoLoading[kind]) return false;
  const seq = (state.modelAutoLoadSeq[kind] || 0) + 1;
  state.modelAutoLoadSeq[kind] = seq;
  state.modelAutoLoading[kind] = true;
  try {
    await loadModels({
      keySelector,
      selectSelector,
      buttonSelector,
      provider: kind === "image" ? state.imageProvider : "openai",
      purpose: kind,
      silent,
      preferredModel: readPreferredModel(kind),
    });
    return state.modelAutoLoadSeq[kind] === seq;
  } finally {
    if (state.modelAutoLoadSeq[kind] === seq) state.modelAutoLoading[kind] = false;
  }
}

async function clearSavedCredentials() {
  const database = await openLocalImageDB();
  await new Promise((resolve, reject) => {
    const transaction = database.transaction("credentials", "readwrite");
    transaction.objectStore("credentials").clear();
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
  $("#imageKey").value = "";
  $("#judgeKey").value = "";
  $("#profileKey").value = "";
  state.profile = null;
  state.profileKey = "";
  state.profileAutoAttempted = false;
  $("#profilePreview").hidden = true;
  $("#profileForm").hidden = true;
  switchProfileStack("identity");
  $("#profileUnlockHint").textContent = "当前浏览器没有已保存的 Key，请填写本人任意有效 Key。";
  const awardButton = $("#awardLookupForm button");
  if (awardButton) {
    awardButton.disabled = true;
    awardButton.textContent = "查询历史排名";
  }
  $("#awardResults").hidden = true;
  $("#awardPagination").hidden = true;
  resetModelSelect("#imageModel", "先读取生图模型");
  resetModelSelect("#judgeModel", "先读取评分模型");
  updateComposerModelPicker();
  toast("已清除当前浏览器保存的密钥");
}

async function saveLocalEntry(dataURL, submission, scoringFailed, modelSnapshot = {}, options = {}) {
  if (!state.contest || !dataURL) return;
  const submissionID = String(submission?.id || modelSnapshot.submission_id || state.submissionId || "").trim();
  if (!submissionID) throw new Error("缺少本地作品编号");
  const database = await openLocalImageDB();
  await new Promise((resolve, reject) => {
    const transaction = database.transaction("submission_entries", "readwrite");
    transaction.objectStore("submission_entries").put({
      contest_id: state.contest.id,
      contest: {
        id: state.contest.id,
        title: state.contest.title,
        prompt: state.contest.prompt,
        allow_prompt_adjustment: state.contest.allow_prompt_adjustment,
        starts_at: state.contest.starts_at,
        ends_at: state.contest.ends_at,
      },
      prompt: state.contest.prompt,
      prompt_adjustment: submission?.prompt_adjustment || modelSnapshot.prompt_adjustment || "",
      submission_id: submissionID,
      image_data_url: dataURL,
      submission: submission || null,
      image_provider: submission?.image_provider || modelSnapshot.image_provider || state.imageProvider,
      image_model: submission?.image_model || modelSnapshot.image_model || "",
      judge_model: submission?.judge_model || modelSnapshot.judge_model || "",
      scoring_failed: Boolean(scoringFailed),
      saved_at: submission?.created_at || new Date().toISOString(),
    });
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
  setSaveStatus();
  if (options.render !== false) await renderLocalSubmissions();
}

function localEntryContestID(entry) {
  return Number(entry?.contest?.id || entry?.contest_id || 0);
}

function localEntryContestTitle(entry) {
  return entry?.contest?.title || (localEntryContestID(entry) ? `比赛 #${localEntryContestID(entry)}` : "未知比赛");
}

function localEntryImageModel(entry) {
  return String(entry?.submission?.image_model || entry?.image_model || "").trim();
}

function localEntryImageProvider(entry) {
  return String(entry?.submission?.image_provider || entry?.image_provider || "openai").trim().toLowerCase() || "openai";
}

function localEntryModelLabel(entry) {
  const model = localEntryImageModel(entry);
  const provider = localEntryImageProvider(entry).toUpperCase();
  return model ? `${provider} · ${model}` : provider;
}

function localEntryModelKey(entry) {
  const model = localEntryImageModel(entry) || "-";
  return `${localEntryImageProvider(entry)}::${model}`;
}

async function readAllLocalEntries() {
  const database = await openLocalImageDB();
  const entries = await new Promise((resolve, reject) => {
    const items = [];
    const request = database.transaction("submission_entries", "readonly").objectStore("submission_entries").index("saved_at").openCursor(null, "prev");
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve(items);
        return;
      }
      items.push(cursor.value);
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });
  database.close();
  return entries;
}

function filterLocalEntries(entries, contestFilter = "all", modelFilter = "all") {
  return entries.filter((entry) => {
    if (contestFilter !== "all" && String(localEntryContestID(entry)) !== String(contestFilter)) return false;
    if (modelFilter !== "all" && localEntryModelKey(entry) !== modelFilter) return false;
    return true;
  });
}

function paginateLocalEntries(entries, page, pageSize) {
  const total = entries.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const current = Math.min(Math.max(page, 1), totalPages);
  const offset = (current - 1) * pageSize;
  return { entries: entries.slice(offset, offset + pageSize), total, page: current, totalPages };
}

function renderLocalFilterOptions(allEntries) {
  const contestSelect = $("#localContestFilter");
  const modelSelect = $("#localModelFilter");
  if (!contestSelect || !modelSelect) return;

  const contests = new Map();
  const models = new Map();
  allEntries.forEach((entry) => {
    const contestID = localEntryContestID(entry);
    if (contestID) contests.set(String(contestID), localEntryContestTitle(entry));
    models.set(localEntryModelKey(entry), localEntryModelLabel(entry));
  });

  const contestValue = state.localContestFilter || "all";
  const modelValue = state.localModelFilter || "all";
  const sortedContests = [...contests.entries()].sort((a, b) => Number(b[0]) - Number(a[0]));
  const sortedModels = [...models.entries()].sort((a, b) => a[1].localeCompare(b[1], "zh-CN"));

  contestSelect.innerHTML = ['<option value="all">全部比赛</option>', ...sortedContests.map(([id, title]) => `<option value="${escapeHTML(id)}">${escapeHTML(title)}</option>`)].join("");
  modelSelect.innerHTML = ['<option value="all">全部模型</option>', ...sortedModels.map(([key, label]) => `<option value="${escapeHTML(key)}">${escapeHTML(label)}</option>`)].join("");

  contestSelect.value = contests.has(contestValue) || contestValue === "all" ? contestValue : "all";
  modelSelect.value = models.has(modelValue) || modelValue === "all" ? modelValue : "all";
  state.localContestFilter = contestSelect.value;
  state.localModelFilter = modelSelect.value;
  contestSelect.disabled = !allEntries.length;
  modelSelect.disabled = !allEntries.length;
}

async function readLocalEntriesPage(page, pageSize, contestFilter = "all", modelFilter = "all") {
  const allEntries = await readAllLocalEntries();
  const filtered = filterLocalEntries(allEntries, contestFilter, modelFilter);
  const paged = paginateLocalEntries(filtered, page, pageSize);
  return { ...paged, allEntries, filteredTotal: filtered.length };
}

function makeSampleImageDataURL(label, colors) {
  const canvas = document.createElement("canvas");
  canvas.width = 320;
  canvas.height = 320;
  const context = canvas.getContext("2d");
  const gradient = context.createLinearGradient(0, 0, 320, 320);
  gradient.addColorStop(0, colors[0]);
  gradient.addColorStop(1, colors[1]);
  context.fillStyle = gradient;
  context.fillRect(0, 0, 320, 320);
  context.fillStyle = "rgba(15, 23, 42, .18)";
  context.fillRect(24, 24, 272, 272);
  context.strokeStyle = "rgba(255,255,255,.55)";
  context.lineWidth = 3;
  context.strokeRect(36, 36, 248, 248);
  context.fillStyle = "#ffffff";
  context.font = '700 28px "Microsoft YaHei UI", "Segoe UI", sans-serif';
  context.fillText("每日一图", 52, 120);
  context.font = '600 18px "Microsoft YaHei UI", "Segoe UI", sans-serif';
  context.fillText(String(label || "示例"), 52, 162);
  context.font = '500 14px "Consolas", monospace';
  context.fillStyle = "rgba(255,255,255,.86)";
  context.fillText("LOCAL DEMO", 52, 250);
  return canvas.toDataURL("image/png");
}

function buildLocalDemoEntries() {
  const now = Date.now();
  const active = state.contest;
  const contests = [
    {
      id: active?.id || 9001,
      title: active?.title || "示例：雨夜霓虹街角",
      prompt: active?.prompt || "雨夜霓虹街角，电影感构图，青橙对比色，低机位广角。",
      allow_prompt_adjustment: true,
      starts_at: active?.starts_at || new Date(now - 3 * 86400000).toISOString(),
      ends_at: active?.ends_at || new Date(now + 2 * 86400000).toISOString(),
    },
    {
      id: 9002,
      title: "示例：晨雾空中列车",
      prompt: "晨雾中的空中列车，金色天光，广角城市天际线。",
      allow_prompt_adjustment: true,
      starts_at: new Date(now - 12 * 86400000).toISOString(),
      ends_at: new Date(now - 5 * 86400000).toISOString(),
    },
    {
      id: 9003,
      title: "示例：深海图书馆",
      prompt: "深海图书馆，玻璃穹顶与鲸群光斑，铜版画质感。",
      allow_prompt_adjustment: false,
      starts_at: new Date(now - 20 * 86400000).toISOString(),
      ends_at: new Date(now - 13 * 86400000).toISOString(),
    },
  ];
  const samples = [
    { id: "local-demo-01", contest: contests[0], provider: "openai", model: "gpt-image-2", judge: "gpt-5.6-sol", score: 94, pending: false, colors: ["#2563eb", "#7b6cff"], label: "霓虹 A" },
    { id: "local-demo-02", contest: contests[0], provider: "gemini", model: "gemini-3-pro-image", judge: "gpt-5.6-luna", score: 88, pending: false, colors: ["#059669", "#14b8a6"], label: "霓虹 B" },
    { id: "local-demo-03", contest: contests[0], provider: "openai", model: "gpt-image-2", judge: "gpt-5.5", score: 0, pending: true, colors: ["#dc2626", "#f97316"], label: "待评分" },
    { id: "local-demo-04", contest: contests[1], provider: "openai", model: "gpt-image-2", judge: "gpt-5.6-sol", score: 91, pending: false, colors: ["#0ea5e9", "#6366f1"], label: "列车 A" },
    { id: "local-demo-05", contest: contests[1], provider: "gemini", model: "gemini-3.1-flash-image", judge: "gpt-5.5", score: 84, pending: false, colors: ["#d97706", "#f59e0b"], label: "列车 B" },
    { id: "local-demo-06", contest: contests[2], provider: "openai", model: "gpt-image-2", judge: "gpt-5.6-luna", score: 96, pending: false, colors: ["#1d4ed8", "#312e81"], label: "图书馆 A" },
    { id: "local-demo-07", contest: contests[2], provider: "gemini", model: "gemini-3-pro-image", judge: "gpt-5.6-sol", score: 79, pending: false, colors: ["#0f766e", "#155e75"], label: "图书馆 B" },
    { id: "local-demo-08", contest: contests[2], provider: "openai", model: "gpt-image-2", judge: "gpt-5.5", score: 0, pending: true, colors: ["#7c3aed", "#db2777"], label: "草稿" },
  ];
  return samples.map((sample, index) => {
    const savedAt = new Date(now - index * 3600000).toISOString();
    const submission = sample.pending
      ? {
          id: sample.id,
          status: "pending_score",
          image_provider: sample.provider,
          image_model: sample.model,
          judge_model: sample.judge,
          official_prompt_snapshot: sample.contest.prompt,
          prompt_adjustment: index % 2 ? "加强主体体积光" : "",
          created_at: savedAt,
          attempt_number: 0,
        }
      : {
          id: sample.id,
          status: "scored",
          image_provider: sample.provider,
          image_model: sample.model,
          judge_model: sample.judge,
          total_score: sample.score,
          prompt_match: Math.min(40, Math.round(sample.score * 0.4)),
          composition: Math.min(25, Math.round(sample.score * 0.25)),
          quality: Math.min(20, Math.round(sample.score * 0.2)),
          creativity: Math.min(10, Math.round(sample.score * 0.1)),
          artifacts: Math.min(5, Math.max(1, Math.round(sample.score * 0.05))),
          judge_comment: `示例评语：${sample.label} 构图稳定，光影与赛题契合。`,
          official_prompt_snapshot: sample.contest.prompt,
          prompt_adjustment: index % 3 === 0 ? "增加前景引导线" : "",
          created_at: savedAt,
          attempt_number: (index % 3) + 1,
        };
    return {
      contest_id: sample.contest.id,
      contest: sample.contest,
      prompt: sample.contest.prompt,
      prompt_adjustment: submission.prompt_adjustment || "",
      submission_id: sample.id,
      image_data_url: makeSampleImageDataURL(sample.label, sample.colors),
      submission,
      image_provider: sample.provider,
      image_model: sample.model,
      judge_model: sample.judge,
      scoring_failed: sample.pending,
      saved_at: savedAt,
      is_demo: true,
    };
  });
}

async function putLocalEntries(entries) {
  if (!entries?.length) return;
  const database = await openLocalImageDB();
  await new Promise((resolve, reject) => {
    const transaction = database.transaction("submission_entries", "readwrite");
    const store = transaction.objectStore("submission_entries");
    entries.forEach((entry) => store.put(entry));
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

async function deleteLocalEntries(ids) {
  const uniqueIDs = [...new Set((ids || []).filter(Boolean))];
  if (!uniqueIDs.length) return 0;
  const database = await openLocalImageDB();
  await new Promise((resolve, reject) => {
    const transaction = database.transaction("submission_entries", "readwrite");
    const store = transaction.objectStore("submission_entries");
    uniqueIDs.forEach((id) => store.delete(id));
    transaction.oncomplete = resolve;
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
  uniqueIDs.forEach((id) => state.localSelectedIDs.delete(id));
  return uniqueIDs.length;
}

function updateLocalSelectionUI(pageEntries = []) {
  const selectAll = $("#localSelectAll");
  const deleteButton = $("#deleteLocalSelected");
  const meta = $("#localSelectionMeta");
  const pageIDs = pageEntries.map((entry) => entry.submission_id).filter(Boolean);
  const selectedCount = state.localSelectedIDs.size;
  const selectedOnPage = pageIDs.filter((id) => state.localSelectedIDs.has(id)).length;
  if (selectAll) {
    selectAll.disabled = !pageIDs.length;
    selectAll.checked = pageIDs.length > 0 && selectedOnPage === pageIDs.length;
    selectAll.indeterminate = selectedOnPage > 0 && selectedOnPage < pageIDs.length;
  }
  if (deleteButton) deleteButton.disabled = selectedCount === 0;
  if (meta) meta.textContent = selectedCount ? `已选 ${selectedCount} 条` : "未选择";
}

function toggleLocalSelection(submissionID, selected) {
  if (!submissionID) return;
  if (selected) state.localSelectedIDs.add(submissionID);
  else state.localSelectedIDs.delete(submissionID);
}

async function seedLocalDemoEntries({ force = false, silent = false } = {}) {
  const existing = await readAllLocalEntries();
  if (!force && existing.length) {
    if (!silent) toast("已有本地作品，如需覆盖示例请再次点击填充示例");
    return false;
  }
  if (force) {
    const demoIDs = existing.filter((entry) => entry.is_demo || String(entry.submission_id || "").startsWith("local-demo-")).map((entry) => entry.submission_id);
    if (demoIDs.length) await deleteLocalEntries(demoIDs);
  }
  await putLocalEntries(buildLocalDemoEntries());
  state.localSelectedIDs = new Set();
  state.localPage = 1;
  if (!silent) toast("已填充 8 条本地示例作品");
  return true;
}

async function handleDeleteLocalSelected() {
  const ids = [...state.localSelectedIDs];
  if (!ids.length) {
    toast("请先勾选要删除的本地作品", "error");
    return;
  }
  if (!await confirmAction({
    title: `删除 ${ids.length} 条本地作品`,
    message: "所选作品的原图、评分结果和本地记录都会被永久删除，此操作无法撤销。",
    confirmLabel: "确认删除",
  })) return;
  try {
    const deleted = await deleteLocalEntries(ids);
    toast(`已删除 ${deleted} 条本地作品`);
    await renderLocalSubmissions();
  } catch (error) {
    toast(error?.message || "删除本地作品失败", "error");
  }
}

async function readLocalEntry(submissionID) {
  const database = await openLocalImageDB();
  const entry = await new Promise((resolve, reject) => {
    const request = database.transaction("submission_entries", "readonly").objectStore("submission_entries").get(submissionID);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return entry;
}

async function readLocalEntriesForContest(contestID) {
  const database = await openLocalImageDB();
  const entries = await new Promise((resolve, reject) => {
    const request = database.transaction("submission_entries", "readonly").objectStore("submission_entries").index("contest_id").getAll(contestID);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return entries.sort((a, b) => String(b.saved_at).localeCompare(String(a.saved_at)));
}

async function restoreLocalEntry() {
  if (!state.contest) return false;
  const preferred = state.submissionId ? await readLocalEntry(state.submissionId) : null;
  const restoreOptions = { allowDemo: state.demoData };
  const saved = isRestorableLocalEntry(preferred, state.contest, restoreOptions)
    ? preferred
    : (await readLocalEntriesForContest(state.contest.id)).find((entry) => isRestorableLocalEntry(entry, state.contest, restoreOptions)) || null;
  if (!saved) return false;
  state.submissionId = saved.submission_id || state.submissionId;
  if (state.submissionId) writeBrowserStorage(`arena-submission-${state.contest.id}`, state.submissionId);
  showGeneratedImage(saved.image_data_url, saved.submission, saved.scoring_failed);
  setSaveStatus();
  setRequestStatus({ status: "success", title: "已恢复本地作品", message: "已根据本场提示词恢复当前浏览器保存的原图和评分。", submissionId: state.submissionId });
  return true;
}

async function recoverMissingContestSubmissions() {
  if (!state.contest) return 0;
  const localEntries = await readLocalEntriesForContest(state.contest.id);
  const knownSubmissionIDs = localEntries.map((entry) => String(entry.submission_id || "").trim()).filter(Boolean);
  const savedCandidates = await savedProfileKeyCandidates();
  const candidates = [...new Set([
    $("#imageKey")?.value.trim(),
    $("#judgeKey")?.value.trim(),
    ...savedCandidates,
  ].filter((key) => keyLooksReady(key)))];
  let recovered = [];
  for (const apiKey of candidates) {
    try {
      const result = await api("/api/submissions/recover", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key: apiKey,
          contest_id: state.contest.id,
          known_submission_ids: knownSubmissionIDs,
        }),
      });
      const entries = Array.isArray(result.entries) ? result.entries : [];
      if (entries.length) {
        recovered = entries;
        break;
      }
    } catch (error) {
      if (!["INVALID_API_KEY", "IDENTITY_LOOKUP_FAILED"].includes(error?.code)) throw error;
    }
  }
  for (const submission of recovered) {
    if (!submission?.id || !submission.image_data_url) continue;
    const { image_data_url: imageDataURL, ...submissionMetadata } = submission;
    await saveLocalEntry(
      imageDataURL,
      submissionMetadata,
      submissionMetadata.status !== "scored",
      { submission_id: submission.id },
      { render: false },
    );
  }
  if (recovered.length) {
    await renderLocalSubmissions();
    toast(`已恢复 ${recovered.length} 个本地缺失作品`);
  }
  return recovered.length;
}

async function recoverContestAfterStreamFailure() {
  for (const delay of [1000, 4000, 10000]) {
    await new Promise((resolve) => window.setTimeout(resolve, delay));
    try {
      if (await recoverMissingContestSubmissions()) {
        await restoreLocalEntry();
        setRequestStatus({
          status: "success",
          title: "已恢复生成作品",
          message: "生成连接曾中断，系统已从短期恢复副本补回本地作品。",
          submissionId: state.submissionId,
        });
        return true;
      }
    } catch {
      // Recovery is best-effort; the original request error remains visible.
    }
  }
  return false;
}

function localScoreDetails(submission) {
  if (!submission || submission.status !== "scored") return '<span class="local-score-pending">评分未完成</span>';
  return `<strong>${Number(submission.total_score) || 0} / 100</strong><span>提示词 ${Number(submission.prompt_match) || 0}/40</span><span>构图 ${Number(submission.composition) || 0}/25</span><span>细节 ${Number(submission.quality) || 0}/20</span><span>创意 ${Number(submission.creativity) || 0}/10</span><span>瑕疵 ${Number(submission.artifacts) || 0}/5</span>`;
}

function localAttemptLabel(submission) {
  if (!submission || (submission.status !== "scored" && submission.status !== "disqualified")) return "待评分 · 未计入提交次数";
  return submission.attempt_number > 0 ? `第 ${submission.attempt_number} 次提交 · 已计入` : "已计入提交次数";
}

function loadShareImage(dataURL) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("本地原图读取失败"));
    image.src = dataURL;
  });
}

function drawImageCover(context, image, x, y, width, height) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const sourceWidth = width / scale;
  const sourceHeight = height / scale;
  const sourceX = (image.naturalWidth - sourceWidth) / 2;
  const sourceY = (image.naturalHeight - sourceHeight) / 2;
  context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
}

function fitCanvasText(context, text, maxWidth) {
  const value = String(text || "-");
  if (context.measureText(value).width <= maxWidth) return value;
  let clipped = value;
  while (clipped.length > 1 && context.measureText(`${clipped}…`).width > maxWidth) clipped = clipped.slice(0, -1);
  return `${clipped}…`;
}

function drawCanvasLines(context, text, x, y, maxWidth, lineHeight, maxLines) {
  const characters = Array.from(String(text || "-"));
  const lines = [];
  let line = "";
  for (const character of characters) {
    if (context.measureText(line + character).width > maxWidth && line) {
      lines.push(line);
      line = character;
      if (lines.length === maxLines) break;
    } else {
      line += character;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  const consumed = lines.join("").length;
  if (consumed < characters.length && lines.length) lines[lines.length - 1] = fitCanvasText(context, lines[lines.length - 1], maxWidth);
  lines.forEach((item, index) => context.fillText(item, x, y + index * lineHeight));
  return y + lines.length * lineHeight;
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("分享卡片生成失败")), "image/png"));
}

async function createShareCard(entry) {
  const submission = entry.submission;
  if (!submission || submission.status !== "scored") throw new Error("评分完成后才能生成分享卡片");
  const contest = entry.contest || { id: entry.contest_id, title: `比赛 #${entry.contest_id}` };
  const image = await loadShareImage(entry.image_data_url);
  if (document.fonts?.ready) await document.fonts.ready;

  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = "#f3f0e8";
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = "#11110f";
  context.fillRect(0, 0, 1080, 76);
  context.fillStyle = "#eaff2f";
  context.fillRect(44, 18, 58, 40);
  context.fillStyle = "#11110f";
  context.font = '900 20px "Arial Narrow", sans-serif';
  context.fillText("C2P", 55, 45);
  context.fillStyle = "#ffffff";
  context.font = '800 22px "PingFang SC", "Microsoft YaHei", sans-serif';
  context.fillText("每日一图 / DAILY FRAME", 126, 47);
  context.textAlign = "right";
  context.font = '700 16px "SFMono-Regular", monospace';
  context.fillStyle = "#a8aaa2";
  context.fillText("SHARE CARD · 1080 × 1350", 1036, 46);
  context.textAlign = "left";

  drawImageCover(context, image, 44, 112, 992, 718);
  context.strokeStyle = "#11110f";
  context.lineWidth = 4;
  context.strokeRect(44, 112, 992, 718);

  context.fillStyle = "#11110f";
  context.font = '900 42px "PingFang SC", "Microsoft YaHei", sans-serif';
  const titleBottom = drawCanvasLines(context, contest.title, 54, 900, 972, 52, 2);

  const scoreTop = Math.max(1008, titleBottom + 22);
  context.fillStyle = "#f04435";
  context.font = '900 76px "Arial Narrow", "DIN Alternate", sans-serif';
  context.fillText(`${Number(submission.total_score) || 0}`, 54, scoreTop + 66);
  context.fillStyle = "#11110f";
  context.font = '800 22px "SFMono-Regular", monospace';
  context.fillText("/ 100", 150, scoreTop + 63);

  const scoreItems = [
    ["提示词", submission.prompt_match, 40],
    ["构图", submission.composition, 25],
    ["细节", submission.quality, 20],
    ["创意", submission.creativity, 10],
    ["瑕疵", submission.artifacts, 5],
  ];
  context.font = '800 17px "SFMono-Regular", "PingFang SC", monospace';
  scoreItems.forEach(([label, score, max], index) => {
    const x = 292 + index * 145;
    context.fillStyle = "#74766f";
    context.fillText(label, x, scoreTop + 22);
    context.fillStyle = "#11110f";
    context.font = '900 28px "Arial Narrow", sans-serif';
    context.fillText(`${Number(score) || 0}/${max}`, x, scoreTop + 58);
    context.font = '800 17px "SFMono-Regular", "PingFang SC", monospace';
  });

  const ruleY = scoreTop + 94;
  context.fillStyle = "#11110f";
  context.fillRect(54, ruleY, 972, 2);
  context.font = '700 18px "SFMono-Regular", "PingFang SC", monospace';
  context.fillStyle = "#11110f";
  const imageProvider = (submission.image_provider || entry.image_provider || "openai").toUpperCase();
  const imageModel = submission.image_model || entry.image_model || "-";
  const judgeModel = submission.judge_model || entry.judge_model || "-";
  context.fillText(fitCanvasText(context, `生图  ${imageProvider} · ${imageModel}`, 560), 54, ruleY + 42);
  context.fillText(fitCanvasText(context, `评分  ${judgeModel}`, 390), 636, ruleY + 42);

  context.fillStyle = "#666962";
  context.font = '500 18px "PingFang SC", "Microsoft YaHei", sans-serif';
  drawCanvasLines(context, submission.judge_comment || "评分完成", 54, ruleY + 88, 972, 29, 2);

  context.fillStyle = "#11110f";
  context.fillRect(44, 1292, 992, 2);
  context.font = '700 15px "SFMono-Regular", monospace';
  context.fillStyle = "#74766f";
  const submittedAt = new Date(submission.created_at || entry.saved_at).toLocaleDateString("zh-CN");
  context.fillText(`CONTEST #${contest.id} · ${submittedAt}`, 44, 1324);
  context.textAlign = "right";
  context.fillText("daily.change2pro.com", 1036, 1324);
  context.textAlign = "left";
  return canvasToBlob(canvas);
}

function downloadShareCard(blob, entry) {
  const link = document.createElement("a");
  const objectURL = URL.createObjectURL(blob);
  link.href = objectURL;
  link.download = `daily-frame-${entry.contest_id}-${entry.submission_id}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectURL), 1000);
}

async function shareLocalEntry(entry, button) {
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = "正在生成 1080×1350";
  try {
    const blob = await createShareCard(entry);
    if (state.shareCard?.objectURL) URL.revokeObjectURL(state.shareCard.objectURL);
    const objectURL = URL.createObjectURL(blob);
    state.shareCard = { blob, entry, objectURL };
    $("#shareCardPreview").src = objectURL;
    $("#shareCardTitle").textContent = entry.contest?.title || `比赛 #${entry.contest_id}`;
    const file = new File([blob], `daily-frame-${entry.contest_id}.png`, { type: "image/png" });
    $("#systemShareCard").hidden = !(navigator.share && navigator.canShare?.({ files: [file] }));
    $("#shareCardDialog").showModal();
    toast("1080×1350 分享卡片已生成");
  } catch (error) {
    toast(error?.message || "分享卡片生成失败", "error");
  } finally {
    button.disabled = false;
    button.textContent = originalText;
  }
}

function closeShareCard() {
  $("#shareCardDialog").close();
  if (state.shareCard?.objectURL) URL.revokeObjectURL(state.shareCard.objectURL);
  state.shareCard = null;
  $("#shareCardPreview").removeAttribute("src");
}

function systemShareCard() {
  if (!state.shareCard || !navigator.share) return;
  const { blob, entry } = state.shareCard;
  const file = new File([blob], `daily-frame-${entry.contest_id}.png`, { type: "image/png" });
  navigator.share({ title: `${entry.contest?.title || "每日一图"} · 我的评分`, text: "我的每日一图参赛成绩", files: [file] })
    .then(() => toast("分享卡片已发送"))
    .catch((error) => { if (error?.name !== "AbortError") toast(error?.message || "系统分享失败", "error"); });
}

async function renderLocalSubmissions() {
  const container = $("#localSubmissionList");
  const pagination = $("#localSubmissionPagination");
  const meta = $("#localFilterMeta");
  let { entries, total, page, allEntries } = await readLocalEntriesPage(
    state.localPage,
    state.localPageSize,
    state.localContestFilter,
    state.localModelFilter,
  );

  if (!allEntries.length && state.demoData) {
    try {
      await seedLocalDemoEntries({ force: false, silent: true });
      ({ entries, total, page, allEntries } = await readLocalEntriesPage(
        1,
        state.localPageSize,
        state.localContestFilter,
        state.localModelFilter,
      ));
      if (allEntries.length) toast("已自动填充本地示例作品，便于界面验证");
    } catch {
      // Demo seeding is best-effort for local UI checks.
    }
  }

  state.localPage = page;
  // Drop selections that no longer exist.
  const existingIDs = new Set(allEntries.map((entry) => entry.submission_id));
  state.localSelectedIDs = new Set([...state.localSelectedIDs].filter((id) => existingIDs.has(id)));
  renderLocalFilterOptions(allEntries);
  if (meta) {
    const filterActive = state.localContestFilter !== "all" || state.localModelFilter !== "all";
    meta.textContent = filterActive
      ? `筛选结果 ${total} 条 · 共 ${allEntries.length} 条`
      : `共 ${allEntries.length} 条`;
  }
  if (!allEntries.length) {
    container.innerHTML = emptyState("暂无本地作品", "生成并参赛后，原图和评分会保存在当前浏览器");
    pagination.hidden = true;
    updateLocalSelectionUI([]);
    return;
  }
  if (!total) {
    container.innerHTML = emptyState("没有匹配的本地作品", "试试切换比赛或生图模型筛选");
    pagination.hidden = true;
    updateLocalSelectionUI([]);
    return;
  }
  container.innerHTML = entries.map((entry) => {
    const contest = entry.contest || { id: entry.contest_id, title: `比赛 #${entry.contest_id}`, prompt: entry.prompt || "旧版本本地记录" };
    const submission = entry.submission;
    const pendingScore = entry.scoring_failed || !submission || submission.status === "pending_score";
    const scored = submission?.status === "scored";
    const extension = entry.image_data_url?.startsWith("data:image/jpeg") ? "jpg" : entry.image_data_url?.startsWith("data:image/webp") ? "webp" : "png";
    const scoreBadge = scored
      ? `<span class="local-score-badge">${Number(submission.total_score) || 0}</span>`
      : `<span class="local-score-badge is-pending">待评</span>`;
    const imageModel = localEntryImageModel(entry) || "-";
    const imageProvider = localEntryImageProvider(entry).toUpperCase();
    const judgeModel = submission?.judge_model || entry.judge_model || "-";
    const checked = state.localSelectedIDs.has(entry.submission_id) ? "checked" : "";
    return `<article class="local-submission-card${checked ? " is-selected" : ""}">
      <label class="local-select">
        <input type="checkbox" data-local-select="${escapeHTML(entry.submission_id)}" ${checked} aria-label="选择 ${escapeHTML(contest.title)}">
      </label>
      <button type="button" class="local-thumb-button" data-local-preview="${escapeHTML(entry.submission_id)}" aria-label="预览 ${escapeHTML(contest.title)} 本地作品">
        <img loading="lazy" src="${entry.image_data_url}" alt="${escapeHTML(contest.title)} 本地参赛作品">
        ${scoreBadge}
      </button>
      <div class="local-submission-detail">
        <h3 title="${escapeHTML(contest.title)}">${escapeHTML(contest.title)}</h3>
        <small>${escapeHTML(new Date(submission?.created_at || entry.saved_at).toLocaleString())} · ${escapeHTML(localAttemptLabel(submission))}</small>
        <div class="local-model"><code title="${escapeHTML(imageProvider)} · ${escapeHTML(imageModel)}">${escapeHTML(imageModel)}</code></div>
        <div class="local-actions">
          <a class="download-command" href="${entry.image_data_url}" download="c2p-arena-${escapeHTML(entry.submission_id)}.${extension}">下载</a>
          <button class="share-command" type="button" data-share-id="${escapeHTML(entry.submission_id)}" ${scored ? "" : "disabled"}>分享</button>
          <button class="local-detail-command" type="button" data-local-detail="${escapeHTML(entry.submission_id)}">详情</button>
          ${pendingScore ? `<button class="retry-command" type="button" data-retry-id="${escapeHTML(entry.submission_id)}">评分</button>` : ""}
        </div>
      </div>
    </article>`;
  }).join("");
  const entriesByID = new Map(entries.map((entry) => [entry.submission_id, entry]));
  container.querySelectorAll("[data-local-select]").forEach((input) => input.addEventListener("change", () => {
    toggleLocalSelection(input.dataset.localSelect, input.checked);
    input.closest(".local-submission-card")?.classList.toggle("is-selected", input.checked);
    updateLocalSelectionUI(entries);
  }));
  container.querySelectorAll("[data-local-preview]").forEach((button) => button.addEventListener("click", () => {
    const entry = entriesByID.get(button.dataset.localPreview);
    if (entry?.image_data_url) openArchiveLightbox(entry.image_data_url, `${localEntryContestTitle(entry)} 本地作品`);
  }));
  container.querySelectorAll("[data-share-id]").forEach((button) => button.addEventListener("click", () => {
    const entry = entriesByID.get(button.dataset.shareId);
    if (entry) shareLocalEntry(entry, button);
  }));
  container.querySelectorAll("[data-local-detail]").forEach((button) => button.addEventListener("click", () => {
    const entry = entriesByID.get(button.dataset.localDetail);
    if (entry) openLocalDetail(entry);
  }));
  container.querySelectorAll("[data-retry-id]").forEach((button) => button.addEventListener("click", () => {
    const entry = entriesByID.get(button.dataset.retryId);
    if (entry) activateRetryEntry(entry);
  }));
  updateLocalSelectionUI(entries);
  renderPagination(pagination, total, state.localPage, state.localPageSize, (page) => {
    state.localPage = page;
    renderLocalSubmissions().then(() => container.scrollIntoView({ behavior: navigationScrollBehavior(), block: "start" }));
  });
}

function applyLocalFilters({ resetPage = true } = {}) {
  state.localContestFilter = $("#localContestFilter")?.value || "all";
  state.localModelFilter = $("#localModelFilter")?.value || "all";
  if (resetPage) state.localPage = 1;
  return renderLocalSubmissions();
}

function resetLocalFilters() {
  state.localContestFilter = "all";
  state.localModelFilter = "all";
  state.localPage = 1;
  if ($("#localContestFilter")) $("#localContestFilter").value = "all";
  if ($("#localModelFilter")) $("#localModelFilter").value = "all";
  return renderLocalSubmissions();
}

async function activateRetryEntry(entry) {
  state.submissionId = entry.submission_id;
  state.imageDataURL = entry.image_data_url;
  writeBrowserStorage(`arena-submission-${entry.contest_id}`, entry.submission_id);
  showGeneratedImage(entry.image_data_url, entry.submission, true);
  if (!$("#retryJudgeKey").value) {
    $("#retryJudgeKey").value = $("#judgeKey").value || await readLocalCredential("judge");
  }
  if (state.judgeModels.length && state.judgeModelsKey === $("#retryJudgeKey").value.trim()) {
    setModelOptions("#retryJudgeModel", state.judgeModels, entry.judge_model || entry.submission?.judge_model || "");
  }
  location.hash = "today";
  $("#resultPanel").scrollIntoView({ behavior: navigationScrollBehavior(), block: "start" });
  setRequestStatus({ status: "idle", title: "待重新评分", message: "已载入这条待评分作品。读取评分模型并主动选择后即可重试，失败不会占用提交次数。", submissionId: state.submissionId });
}

function updateSubmissionQuota(quota) {
  if (quota) state.submissionQuota = quota;
  const nodes = [$("#submissionQuota"), $("#submissionQuotaFallback")].filter(Boolean);
  if (!nodes.length) return;
  if (!state.submissionQuota) {
    nodes.forEach((node) => {
      node.hidden = true;
      node.textContent = "";
      node.title = "";
      node.removeAttribute("aria-label");
      node.classList.remove("exhausted");
    });
    syncComposerControls();
    return;
  }
  const { remaining, limit, used, extra } = state.submissionQuota;
  const detail = `本场剩余 ${remaining} / ${limit} 次，已使用 ${used} 次${extra ? `，管理员追加 ${extra} 次` : ""}`;
  const label = `剩 ${remaining}/${limit}${extra ? ` +${extra}` : ""}`;
  const adjustmentOpen = Boolean(state.contest?.allow_prompt_adjustment);
  nodes.forEach((node) => {
    const isFallback = node.id === "submissionQuotaFallback";
    // Primary quota sits beside "提示词微调"; fallback only when that field is hidden.
    const shouldShow = isFallback ? !adjustmentOpen : adjustmentOpen;
    node.hidden = !shouldShow;
    node.textContent = shouldShow ? label : "";
    node.title = shouldShow ? detail : "";
    if (shouldShow) node.setAttribute("aria-label", detail);
    else node.removeAttribute("aria-label");
    node.classList.toggle("exhausted", remaining <= 0);
  });
  syncComposerControls();
}

async function api(path, options = {}) {
  const response = await fetch(path, options);
  let data = {};
  try { data = await response.json(); } catch { data = {}; }
  if (!response.ok) throw requestError(response.status, data);
  return data;
}

function requestError(status, data = {}) {
  const error = new Error(data?.error?.message || `请求失败 (${status})`);
  error.code = data?.error?.code || "REQUEST_FAILED";
  error.data = data;
  return error;
}

async function withRefreshState(button, action) {
  if (!button || button.dataset.loading === "true") return;
  const startedAt = Date.now();
  const wasDisabled = button.disabled;
  const idleLabel = button.getAttribute("aria-label") || "刷新";
  button.disabled = true;
  button.dataset.loading = "true";
  button.classList.add("is-loading");
  button.setAttribute("aria-busy", "true");
  button.setAttribute("aria-label", `正在${idleLabel}`);
  try {
    return await action();
  } finally {
    const remaining = 320 - (Date.now() - startedAt);
    if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
    button.disabled = wasDisabled;
    button.dataset.loading = "false";
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    button.setAttribute("aria-label", idleLabel);
  }
}

async function streamSubmission(options, onImage) {
  let response;
  try {
    response = await fetch("/api/submissions/stream", options);
  } catch {
    throw requestError(502, { error: { code: "STREAM_INTERRUPTED", message: "生成请求启动失败，请稍后重试" } });
  }
  if (!response.ok || !response.body) {
    let data = {};
    try { data = await response.json(); } catch { data = {}; }
    if (!response.ok) throw requestError(response.status, data);
    throw requestError(500, data);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let generatedImage = null;
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, "\n");
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop() || "";
      for (const block of blocks) {
        const lines = block.split("\n");
        const event = lines.find((line) => line.startsWith("event:"))?.slice(6).trim();
        const dataText = lines.filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trimStart()).join("\n");
        if (event === "image") {
          try { generatedImage = JSON.parse(dataText); } catch { throw requestError(500, {}); }
          if (onImage) await onImage(generatedImage);
          continue;
        }
        if (event !== "result") continue;
        let envelope;
        try { envelope = JSON.parse(dataText); } catch { throw requestError(500, {}); }
        const body = generatedImage ? { ...generatedImage, ...(envelope.body || {}) } : envelope.body;
        if (envelope.status < 200 || envelope.status >= 400) throw requestError(envelope.status, body);
        return body;
      }
      if (done) break;
    }
  } catch (error) {
    if (error?.code) throw error;
    const data = generatedImage ? { ...generatedImage, error: { code: "SCORING_CONNECTION_INTERRUPTED", message: "图片已保存在当前浏览器，评分未完成，可重新评分" } } : { error: { code: "STREAM_INTERRUPTED", message: "生成流程提前结束，请稍后重试" } };
    throw requestError(502, data);
  }
  const data = generatedImage ? { ...generatedImage, error: { code: "SCORING_CONNECTION_INTERRUPTED", message: "图片已保存在当前浏览器，评分未完成，可重新评分" } } : { error: { code: "STREAM_ENDED", message: "生成流程提前结束，请稍后重试" } };
  throw requestError(502, data);
}

function toast(message, type = "info") {
  const node = $("#toast");
  if (!node) return;
  node.textContent = message;
  node.className = `toast show${type === "error" ? " error" : ""}`;
  clearTimeout(node._timer);
  node._timer = setTimeout(() => { node.className = "toast"; }, 3600);
}

function setRequestStatus({ status = "idle", title = "", message = "", code = "", submissionId = "" }) {
  const panel = $("#requestStatus");
  if (!panel) return;
  // Bottom-left status chip only surfaces failures/exceptions.
  if (status !== "error") {
    panel.hidden = true;
    panel.className = "request-status idle";
    panel.classList.remove("has-copy", "has-code", "has-submission", "has-badge");
    return;
  }
  const badgeText = "失败";
  panel.className = "request-status error";
  $("#requestStatusBadge").textContent = badgeText;
  $("#requestStatusTitle").textContent = title || "请求失败";
  $("#requestStatusMessage").textContent = message || "请稍后重试。";
  $("#requestStatusCode").textContent = code || "-";
  $("#requestStatusSubmission").textContent = submissionId || "-";
  panel.classList.toggle("has-copy", Boolean(title || message));
  panel.classList.toggle("has-code", Boolean(code));
  panel.classList.toggle("has-submission", Boolean(submissionId));
  panel.classList.add("has-badge");
  panel.hidden = false;
}

function showRequestFailure(error, title, fallbackSubmissionId = "") {
  setRequestStatus({
    status: "error",
    title,
    message: error?.message || "请求失败，请稍后重试。",
    code: error?.code || "REQUEST_FAILED",
    submissionId: error?.data?.submission_id || fallbackSubmissionId,
  });
  toast(error?.message || "请求失败", "error");
}

function generationSettingsReady() {
  return Boolean(
    $("#imageKey").value.trim() &&
    $("#judgeKey").value.trim() &&
    $("#imageModel").dataset.ready === "true" &&
    $("#imageModel").value &&
    $("#judgeModel").dataset.ready === "true" &&
    $("#judgeModel").value,
  );
}

function firstMissingGenerationControl() {
  if (!$("#imageKey").value.trim()) return $("#imageKey");
  if ($("#imageModel").dataset.ready !== "true" || !$("#imageModel").value) return $("#loadImageModels");
  if (!$("#judgeKey").value.trim()) return $("#judgeKey");
  if ($("#judgeModel").dataset.ready !== "true" || !$("#judgeModel").value) return $("#loadJudgeModels");
  return $("#confirmGenerationSettings");
}

function modelSelectState(kind) {
  const select = $(kind === "judge" ? "#judgeModel" : "#imageModel");
  const options = [...(select?.options || [])].map((option) => option.value).filter(Boolean);
  const ready = select?.dataset.ready === "true" && options.length > 0;
  return {
    select,
    options,
    ready,
    value: ready ? (select.value || options[0] || "") : "",
    placeholder: select?.options?.[0]?.textContent || "尚未读取",
    hasKey: Boolean($(kind === "judge" ? "#judgeKey" : "#imageKey")?.value.trim()),
  };
}

function closeComposerModelMenu() {
  const menu = $("#composerModelMenu");
  const trigger = $("#composerModelTrigger");
  if (!menu || !trigger) return;
  menu.hidden = true;
  trigger.setAttribute("aria-expanded", "false");
  state.composerModelMenuOpen = false;
  state.composerModelMenuLevel = "root";
  $("#composerModelPanel")?.querySelectorAll(".composer-menu-item.is-open").forEach((item) => item.classList.remove("is-open"));
}

function composerModelValueLabel(kind) {
  const current = modelSelectState(kind);
  if (current.ready && current.value) return current.value;
  if (current.hasKey) return "待读取";
  return "未设置";
}

function updateComposerModelPicker() {
  const summary = $("#composerModelSummary");
  const trigger = $("#composerModelTrigger");
  if (!summary || !trigger) return;
  const image = modelSelectState("image");
  const judge = modelSelectState("judge");
  if (image.ready && judge.ready) {
    summary.textContent = `${image.value} · ${judge.value}`;
    trigger.dataset.ready = "true";
  } else if (image.ready || judge.ready) {
    summary.textContent = `${composerModelValueLabel("image")} · ${composerModelValueLabel("judge")}`;
    trigger.dataset.ready = "partial";
  } else if (image.hasKey || judge.hasKey) {
    summary.textContent = "选择模型";
    trigger.dataset.ready = "keys";
  } else {
    summary.textContent = "模型未设置";
    trigger.dataset.ready = "empty";
  }
  if (state.composerModelMenuOpen) renderComposerModelPanel();
}

function composerModelSubhead(kind, disabled = false) {
  const provider = kind === "judge" || state.imageProvider !== "gemini" ? "OpenAI" : "Gemini";
  return `<header class="composer-model-subhead"><button type="button" class="composer-menu-back" data-composer-model-back aria-label="返回接口与模型"><svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg></button><strong>${provider}</strong><button type="button" class="composer-model-refresh" data-composer-model-load="${kind}" title="刷新模型列表" aria-label="刷新${kind === "judge" ? "评分" : "生图"}模型列表"${disabled ? " disabled" : ""}><svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/></svg></button></header>`;
}

function composerMenuChevron() {
  return '<svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>';
}

function renderComposerSubmenuBody(kind) {
  const title = kind === "judge" ? "评分模型" : "生图模型";
  const current = modelSelectState(kind);
  const subhead = composerModelSubhead(kind, !current.hasKey);
  if (!current.hasKey) {
    return `${subhead}<div class="composer-model-empty"><strong>还没有${kind === "judge" ? "评分" : "生图"}密钥</strong><span>先填写 API Key，再读取模型列表。</span><button type="button" class="composer-menu-action" data-composer-model-settings>去生成设置</button></div>`;
  }
  if (!current.ready) {
    return `${subhead}<div class="composer-model-empty"><strong>${escapeHTML(current.placeholder)}</strong><span>读取后可直接切换当前 Key 的模型。</span><button type="button" class="composer-menu-action" data-composer-model-load="${kind}">读取模型</button></div>`;
  }
  if (current.options.length <= 1) {
    const only = current.options[0] || current.value || "-";
    return `${subhead}<div class="composer-model-list" role="listbox" aria-label="${title}列表">
      <button type="button" role="option" class="composer-model-option is-selected" data-composer-model-kind="${kind}" data-composer-model-value="${escapeHTML(only)}" aria-selected="true"><span>${escapeHTML(only)}</span><small>当前</small></button>
    </div>`;
  }
  return `${subhead}<div class="composer-model-list" role="listbox" aria-label="${title}列表">${current.options.map((model) => {
    const selected = model === current.value;
    return `<button type="button" role="option" class="composer-model-option${selected ? " is-selected" : ""}" data-composer-model-kind="${kind}" data-composer-model-value="${escapeHTML(model)}" aria-selected="${selected}"><span>${escapeHTML(model)}</span>${selected ? "<small>当前</small>" : ""}</button>`;
  }).join("")}</div>`;
}

function positionComposerSubmenu(item) {
  const submenu = item?.querySelector(".composer-submenu");
  const menu = $("#composerModelMenu");
  if (!submenu || !menu) return;
  submenu.classList.remove("is-flip-left", "is-flip-up");
  const menuRect = menu.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const estimatedWidth = Math.min(280, window.innerWidth - 24);
  const estimatedHeight = Math.min(320, window.innerHeight - 24);
  const spaceRight = window.innerWidth - menuRect.right;
  const spaceLeft = menuRect.left;
  // Picker sits near the send button (right side); prefer left flyout when right space is tight.
  if (spaceRight < estimatedWidth + 16 || spaceLeft > spaceRight + 40) submenu.classList.add("is-flip-left");
  const viewportMargin = 10;
  const submenuHeight = Math.min(submenu.scrollHeight || estimatedHeight, window.innerHeight - viewportMargin * 2);
  const spaceBelow = window.innerHeight - itemRect.top - viewportMargin;
  const spaceAbove = itemRect.bottom - viewportMargin;
  if (spaceBelow < submenuHeight && spaceAbove > spaceBelow) submenu.classList.add("is-flip-up");
}

function setComposerSubmenuOpen(kind) {
  const panel = $("#composerModelPanel");
  if (!panel) return;
  const level = kind === "image" || kind === "judge" ? kind : "root";
  state.composerModelMenuLevel = level;
  panel.dataset.level = level;
  panel.querySelectorAll(".composer-menu-item[data-submenu]").forEach((item) => {
    const open = item.dataset.submenu === level;
    item.classList.toggle("is-open", open);
    item.querySelector(".composer-menu-row")?.setAttribute("aria-expanded", String(open));
    if (open) positionComposerSubmenu(item);
  });
}

function bindComposerModelPanelEvents(panel) {
  panel.querySelectorAll(".composer-menu-item[data-submenu]").forEach((item) => {
    const kind = item.dataset.submenu;
    const row = item.querySelector(".composer-menu-row");
    item.addEventListener("mouseenter", () => setComposerSubmenuOpen(kind));
    item.addEventListener("focusin", () => {
      if (row?.matches(":focus-visible")) setComposerSubmenuOpen(kind);
    });
    row?.addEventListener("click", (event) => {
      // Mouse enter may run before click; clicks always enter the selected level.
      event.preventDefault();
      setComposerSubmenuOpen(kind);
    });
  });
  panel.querySelector(".composer-menu-settings")?.addEventListener("mouseenter", () => setComposerSubmenuOpen("root"));
  panel.querySelector(".composer-menu-settings")?.addEventListener("focusin", () => setComposerSubmenuOpen("root"));

  panel.querySelectorAll("[data-composer-model-back]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      setComposerSubmenuOpen("root");
      $("#composerModelTrigger")?.focus({ preventScroll: true });
    });
  });

  panel.querySelectorAll("[data-composer-model-value]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const kind = button.dataset.composerModelKind === "judge" ? "judge" : "image";
      const current = modelSelectState(kind);
      if (!current.select) return;
      current.select.value = button.dataset.composerModelValue;
      current.select.dispatchEvent(new Event("change", { bubbles: true }));
      writePreferredModel(kind, current.select.value);
      updateComposerModelPicker();
      renderComposerModelPanel();
      setComposerSubmenuOpen(kind);
      toast(`${kind === "judge" ? "评分" : "生图"}模型已切换`);
    });
  });
  panel.querySelectorAll("[data-composer-model-load]").forEach((button) => {
    button.addEventListener("click", async (event) => {
      event.preventDefault();
      event.stopPropagation();
      const kind = button.dataset.composerModelLoad === "judge" ? "judge" : "image";
      state.composerModelMenuLevel = kind;
      button.disabled = true;
      button.classList.add("is-loading");
      try {
        await loadComposerModelForActiveTab();
      } catch {
        // loadModels already surfaces failures
      } finally {
        button.disabled = false;
        button.classList.remove("is-loading");
      }
    });
  });
  panel.querySelectorAll("[data-composer-model-settings]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeComposerModelMenu();
      openGenerationSettings("可在此填写密钥并读取模型。");
    });
  });
}

function renderComposerModelPanel() {
  const panel = $("#composerModelPanel");
  if (!panel) return;
  const imageLabel = composerModelValueLabel("image");
  const judgeLabel = composerModelValueLabel("judge");
  const imageProviderLabel = state.imageProvider === "gemini" ? "Gemini" : "OpenAI";
  const active = state.composerModelMenuLevel === "image" || state.composerModelMenuLevel === "judge"
    ? state.composerModelMenuLevel
    : "";
  panel.dataset.level = active || "root";
  panel.innerHTML = `
    <div class="composer-menu-title">接口与模型</div>
    <div class="composer-menu-list" role="none">
      <div class="composer-menu-item${active === "image" ? " is-open" : ""}" data-submenu="image">
        <button type="button" class="composer-menu-row" role="menuitem" aria-haspopup="menu" aria-expanded="${active === "image"}">
          <span class="composer-menu-copy"><strong>生图模型</strong><small>${imageProviderLabel} · ${escapeHTML(imageLabel)}</small></span>
          <span class="composer-menu-value">${composerMenuChevron()}</span>
        </button>
        <div class="composer-submenu" role="menu" aria-label="生图模型列表">
          ${renderComposerSubmenuBody("image")}
        </div>
      </div>
      <div class="composer-menu-item${active === "judge" ? " is-open" : ""}" data-submenu="judge">
        <button type="button" class="composer-menu-row" role="menuitem" aria-haspopup="menu" aria-expanded="${active === "judge"}">
          <span class="composer-menu-copy"><strong>评分模型</strong><small>OpenAI · ${escapeHTML(judgeLabel)}</small></span>
          <span class="composer-menu-value">${composerMenuChevron()}</span>
        </button>
        <div class="composer-submenu" role="menu" aria-label="评分模型列表">
          ${renderComposerSubmenuBody("judge")}
        </div>
      </div>
      <button type="button" class="composer-menu-row composer-menu-settings" data-composer-model-settings role="menuitem">
        <svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></svg>
        <span>生成与评分设置</span>
      </button>
    </div>`;
  bindComposerModelPanelEvents(panel);
  if (active) {
    const item = panel.querySelector(`.composer-menu-item[data-submenu="${active}"]`);
    if (item) positionComposerSubmenu(item);
  }
}

function openComposerModelMenu(level = "root") {
  state.composerModelMenuLevel = level === "image" || level === "judge" ? level : "root";
  const menu = $("#composerModelMenu");
  const trigger = $("#composerModelTrigger");
  if (!menu || !trigger) return;
  menu.hidden = false;
  trigger.setAttribute("aria-expanded", "true");
  state.composerModelMenuOpen = true;
  renderComposerModelPanel();
}

function toggleComposerModelMenu() {
  if (state.composerModelMenuOpen) closeComposerModelMenu();
  else openComposerModelMenu("root");
}

async function loadComposerModelForActiveTab() {
  const tab = state.composerModelMenuLevel === "judge" ? "judge" : "image";
  if (tab === "image") {
    await loadModels({
      keySelector: "#imageKey",
      selectSelector: "#imageModel",
      buttonSelector: "#loadImageModels",
      provider: state.imageProvider,
      purpose: "image",
    });
  } else {
    await loadModels({
      keySelector: "#judgeKey",
      selectSelector: "#judgeModel",
      buttonSelector: "#loadJudgeModels",
      provider: "openai",
      purpose: "judge",
    });
  }
  openComposerModelMenu(tab);
}

function openGenerationSettings(message = "读取模型后即可发送。") {
  const dialog = $("#generationSettingsDialog");
  $("#generationSettingsHint").textContent = message;
  if (!dialog.open) dialog.showModal();
  setTimeout(() => firstMissingGenerationControl()?.focus(), 0);
}

function closeGenerationSettings() {
  $("#generationSettingsDialog").close();
  $("#generationSettingsHint").textContent = "读取模型后即可发送。";
  updateComposerModelPicker();
}

function ensureGenerationSettingsReady() {
  if (generationSettingsReady()) return true;
  setRequestStatus({ status: "idle", title: "需要设置" });
  openGenerationSettings("请补全 Key 和模型。");
  toast("请先完成生成设置");
  return false;
}

function escapeHTML(value = "") {
  return String(value).replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char]);
}

function emptyState(title, note, className = "board-empty") {
  return `<div class="empty-state ${className}"><strong>${escapeHTML(title)}</strong><span>${escapeHTML(note)}</span></div>`;
}

function podiumClass(rank) {
  return ({ 1: "rank-first", 2: "rank-second", 3: "rank-third" })[Number(rank)] || "";
}

function renderPagination(container, totalItems, page, pageSize, onPage) {
  const totalPages = Math.ceil(totalItems / pageSize);
  if (totalPages <= 1) { container.hidden = true; container.innerHTML = ""; return; }
  const current = Math.min(Math.max(page, 1), totalPages);
  container.hidden = false;
  container.innerHTML = `<button type="button" data-page="prev" ${current === 1 ? "disabled" : ""}>上一页</button><span>${current} / ${totalPages}</span><button type="button" data-page="next" ${current === totalPages ? "disabled" : ""}>下一页</button><label>跳至 <input type="number" min="1" max="${totalPages}" value="${current}" aria-label="页码"></label><button type="button" data-page="jump">跳转</button>`;
  container.querySelector('[data-page="prev"]')?.addEventListener("click", () => onPage(current - 1));
  container.querySelector('[data-page="next"]')?.addEventListener("click", () => onPage(current + 1));
  const jump = () => onPage(Math.min(Math.max(Number(container.querySelector("input").value) || 1, 1), totalPages));
  container.querySelector('[data-page="jump"]')?.addEventListener("click", jump);
  container.querySelector("input")?.addEventListener("keydown", (event) => { if (event.key === "Enter") { event.preventDefault(); jump(); } });
}

function switchPublicView(view) {
  const selected = ["contest", "leaderboard", "local", "profile", "archive"].includes(view) ? view : "contest";
  const pageMeta = {
    contest: ["每日一图", "每日图片赛"],
    leaderboard: ["实时排行", "本场比赛榜单"],
    local: ["本地作品", "当前浏览器保存的作品"],
    profile: ["个人中心", "公开资料、历史比赛与奖励"],
    archive: ["往期获奖", "历届公开获奖作品"],
  };
  $(".app-main")?.classList.toggle("is-contest-workspace", selected === "contest");
  document.querySelectorAll("[data-view-panel]").forEach((panel) => { panel.hidden = panel.dataset.viewPanel !== selected; });
  document.querySelectorAll("[data-view-tab]").forEach((button) => { button.setAttribute("aria-selected", String(button.dataset.viewTab === selected)); });
  window.ArenaShell?.setPage(...pageMeta[selected]);
  window.ArenaShell?.closeMobile();
  history.replaceState(null, "", selected === "contest" ? location.pathname : `#${selected}`);
  if (selected === "leaderboard") loadLeaderboard();
  if (selected === "local") renderLocalSubmissions();
  if (selected === "profile") loadProfileAutomatically();
  if (selected === "archive") loadArchive();
  window.scrollTo({ top: 0, behavior: navigationScrollBehavior() });
}

function contestPhase() {
  return contestPhaseFor(state.contest);
}

function contestPhaseFor(contest) {
  if (!contest) return "none";
  const now = Date.now();
  if (now < new Date(contest.starts_at).getTime()) return "upcoming";
  if (now >= new Date(contest.ends_at).getTime()) return "ended";
  return "live";
}

function renderPrizeText(text) {
  const value = String(text || "").trim();
  const node = $("#prizeText");
  const separator = value.match(/^(.{1,12}?)[：:]\s*(.+)$/);
  if (!separator) {
    node.innerHTML = `<span class="prize-summary-values">${escapeHTML(value)}</span>`;
    return;
  }
  const values = separator[2].replace(/\s*\/\s*/g, " / ");
  node.innerHTML = `<span class="prize-summary-label">${escapeHTML(separator[1])}</span><span class="prize-summary-values">${escapeHTML(values)}</span>`;
}

function renderContest() {
  const contest = state.contest;
  if (!contest) return;
  renderHeroBackgrounds(contest.hero_backgrounds);
  $("#contestTitle").textContent = contest.title;
  renderHeroTextStyle(contest);
  fitMobileHeroTitle();
  $("#contestPrompt").textContent = contest.prompt;
  const adjustmentEnabled = Boolean(contest.allow_prompt_adjustment);
  const adjustmentField = $("#promptAdjustmentField");
  const adjustmentInput = $("#promptAdjustment");
  const adjustmentDisabledHint = $("#promptAdjustmentDisabledHint");
  adjustmentField.hidden = !adjustmentEnabled;
  adjustmentInput.disabled = !adjustmentEnabled;
  adjustmentDisabledHint.hidden = adjustmentEnabled;
  $("#entryForm").classList.toggle("no-adjustment", !adjustmentEnabled);
  adjustmentInput.maxLength = Number(contest.prompt_adjustment_max_length) || 500;
  updatePromptAdjustmentCounter();
  updateSubmissionQuota();
  const winnerCount = Number(contest.winner_count);
	renderPrizeText(contest.prize_text || `前 ${winnerCount} 名奖励`);
  $("#winnerCountInline").textContent = Number.isInteger(winnerCount) && winnerCount > 0 ? String(winnerCount) : "--";
  updateClock();
  clearInterval(state.timer);
  state.timer = setInterval(updateClock, 1000);
}

function renderContestLoadError() {
  $("#view-contest").classList.remove("is-loading");
}

function renderHeroTextStyle(contest) {
  const heroCopy = $("#contestHero .hero-copy");
  const setOrClear = (name, value) => {
    if (value) heroCopy.style.setProperty(name, value);
    else heroCopy.style.removeProperty(name);
  };
  setOrClear("--hero-title-color", contest.hero_title_color);
  setOrClear("--hero-countdown-color", contest.hero_countdown_color);
  const titleSize = Math.min(Math.max(Number(contest.hero_title_font_size) || 32, 18), 56);
  const countdownSize = Math.min(Math.max(Number(contest.hero_countdown_font_size) || 18, 12), 36);
  const titleXValue = Number(contest.hero_title_x);
  const titleX = Math.min(Math.max(Number.isFinite(titleXValue) ? titleXValue : 5, 0), 80);
  const titleY = Math.min(Math.max(Number(contest.hero_title_y) || 40, 10), 90);
  const countdownXValue = Number(contest.hero_countdown_x);
  const countdownX = Math.min(Math.max(Number.isFinite(countdownXValue) ? countdownXValue : 5, 0), 80);
  const countdownY = Math.min(Math.max(Number(contest.hero_countdown_y) || 62, 10), 90);
  heroCopy.style.setProperty("--hero-title-size", `${titleSize}px`);
  heroCopy.style.setProperty("--hero-countdown-size", `${countdownSize}px`);
  heroCopy.style.setProperty("--hero-countdown-label-size", `${Math.max(12, Math.round(countdownSize * 2 / 3))}px`);
  heroCopy.style.setProperty("--hero-title-x", `${titleX}%`);
  heroCopy.style.setProperty("--hero-title-y", `${titleY}%`);
  heroCopy.style.setProperty("--hero-countdown-x", `${countdownX}%`);
  heroCopy.style.setProperty("--hero-countdown-y", `${countdownY}%`);
}

function fitMobileHeroTitle() {
  const heroCopy = $("#contestHero .hero-copy");
  const title = $("#contestTitle");
  cancelAnimationFrame(state.heroTitleFitFrame);
  state.heroTitleFitFrame = null;
  heroCopy.style.removeProperty("--hero-title-mobile-size");
  if (!window.matchMedia("(max-width: 700px)").matches) return;

  state.heroTitleFitFrame = requestAnimationFrame(() => {
    state.heroTitleFitFrame = requestAnimationFrame(() => {
      state.heroTitleFitFrame = null;
      const configuredSize = Number.parseFloat(getComputedStyle(heroCopy).getPropertyValue("--hero-title-size")) || 32;
      const scaledSize = Math.max(8, configuredSize * heroCopy.clientHeight / 230);
      heroCopy.style.setProperty("--hero-title-mobile-size", `${Math.floor(scaledSize * 10) / 10}px`);
      const availableWidth = title.clientWidth;
      const contentWidth = title.scrollWidth;
      if (!availableWidth || contentWidth <= availableWidth + 1) return;
      const baseSize = Number.parseFloat(getComputedStyle(title).fontSize) || 18;
      const fittedSize = Math.max(8, Math.floor(baseSize * availableWidth / contentWidth * 10) / 10);
      heroCopy.style.setProperty("--hero-title-mobile-size", `${fittedSize}px`);
    });
  });
}

function renderHeroBackgrounds(backgrounds) {
  clearInterval(state.heroBackgroundTimer);
  state.heroBackgroundTimer = null;
  const paths = Array.isArray(backgrounds) ? backgrounds : [];
  const container = $("#heroBackgrounds");
  const heroCopy = container.closest(".hero-copy");
  const markReady = () => $("#view-contest").classList.remove("is-loading");
  const markReadyAfterFirstImage = () => {
    const firstImage = container.querySelector("img.is-active");
    if (!firstImage) {
      markReady();
      return;
    }
    if (firstImage.complete && firstImage.naturalWidth > 0) {
      markReady();
      return;
    }
    firstImage.addEventListener("load", markReady, { once: true });
    firstImage.addEventListener("error", markReady, { once: true });
    setTimeout(markReady, 2400);
  };
  const bannerPath = (path) => path.replace(/\.png$/, "-banner.jpg");
  const applyTheme = (path) => { heroCopy.dataset.heroBackground = path.includes("bk2") ? "light" : "dark"; };
  if (!paths.length) {
    container.innerHTML = '<img class="is-active" src="/backgrounds/bk1-banner.jpg" alt="" decoding="async" fetchpriority="high">';
    heroCopy.dataset.heroBackground = "dark";
    markReadyAfterFirstImage();
    return;
  }
  container.innerHTML = `${paths.map((path, index) => {
    const source = escapeHTML(bannerPath(path));
    return index === 0
      ? `<img class="is-active" src="${source}" alt="" decoding="async" fetchpriority="high">`
      : `<img data-src="${source}" alt="" loading="lazy" decoding="async">`;
  }).join("")}${paths.length > 1 ? `<div class="hero-background-indicator">${paths.map((_, index) => `<span class="${index === 0 ? "is-active" : ""}"></span>`).join("")}</div>` : ""}`;
  applyTheme(paths[0]);
  markReadyAfterFirstImage();
  if (paths.length < 2) return;
  let activeIndex = 0;
  const images = [...container.querySelectorAll("img")];
  const indicators = [...container.querySelectorAll(".hero-background-indicator span")];
  state.heroBackgroundTimer = setInterval(() => {
    images[activeIndex].classList.remove("is-active");
    indicators[activeIndex].classList.remove("is-active");
    activeIndex = (activeIndex + 1) % images.length;
    if (!images[activeIndex].getAttribute("src")) images[activeIndex].src = images[activeIndex].dataset.src;
    images[activeIndex].classList.add("is-active");
    indicators[activeIndex].classList.add("is-active");
    applyTheme(paths[activeIndex]);
  }, 7000);
}

function updatePromptAdjustmentCounter() {
  const input = $("#promptAdjustment");
  if (!input) return;
  const maxLength = Number(input.maxLength) || Number(state.contest?.prompt_adjustment_max_length) || 500;
  const length = Array.from(input.value || "").length;
  const counter = $("#promptAdjustmentCounter");
  if (counter) counter.textContent = `${length} / ${maxLength}`;
  syncComposerControls();
}

function resetModelSelect(id, message) {
  const select = $(id);
  select.innerHTML = `<option value="">${escapeHTML(message)}</option>`;
  select.dataset.ready = "false";
  select.disabled = true;
  if (id === "#imageModel" || id === "#judgeModel") updateComposerModelPicker();
}

function updateGenerationSettingsControl(element) {
  if (!element.closest("#generationSettingsDialog")) return false;
  const waitingForModels = (element.id === "imageModel" || element.id === "judgeModel") && element.dataset.ready !== "true";
  element.disabled = waitingForModels || element.dataset.loading === "true";
  return true;
}

function setModelOptions(id, models, defaultModel = "") {
  const select = $(id);
  const preferred = defaultModel || preferredModelForSelect(id);
  select.innerHTML = models.map((model) => `<option value="${escapeHTML(model)}">${escapeHTML(model)}</option>`).join("");
  select.dataset.ready = "true";
  select.disabled = false;
  if (preferred && models.includes(preferred)) select.value = preferred;
  else if (defaultModel && models.includes(defaultModel)) select.value = defaultModel;
  if (id === "#imageModel") writePreferredModel("image", select.value);
  if (id === "#judgeModel" || id === "#retryJudgeModel") writePreferredModel("judge", select.value);
  if (id === "#imageModel" || id === "#judgeModel") updateComposerModelPicker();
}

async function loadModels({ keySelector, selectSelector, buttonSelector, provider, purpose, silent = false, preferredModel = "" }) {
  const key = $(keySelector).value.trim();
  if (!key) {
    const error = new Error("请先填写对应的 API Key");
    error.code = "MISSING_KEY";
    if (!silent) showRequestFailure(error, "模型读取失败");
    return false;
  }
  const button = $(buttonSelector);
  const previousLabel = button?.textContent || "读取模型";
  if (button) {
    button.disabled = true;
    button.dataset.loading = "true";
    button.textContent = "读取中";
  }
  resetModelSelect(selectSelector, "正在读取模型…");
  if (!silent) setRequestStatus({ status: "running", title: "正在读取模型", message: "正在验证 API Key 并获取该分组可用模型。" });
  try {
    const result = await api("/api/models", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, provider, purpose }),
    });
    const preferred = preferredModel || preferredModelForSelect(selectSelector) || result.default_model || "";
    setModelOptions(selectSelector, result.models, preferred);
    if (purpose === "image") updateSubmissionQuota(result.submission_quota || null);
    if (purpose === "judge") {
      state.judgeModels = result.models;
      state.judgeModelsKey = key;
      if (selectSelector !== "#retryJudgeModel" && !$("#retryForm").hidden) {
        $("#retryJudgeKey").value = key;
        setModelOptions("#retryJudgeModel", result.models, $("#judgeModel").value || preferred || result.default_model || "");
      }
    }
    try {
      await saveLocalCredential(purpose === "image" ? `image:${provider}` : "judge", key);
    } catch {
      if (!silent) toast("模型读取成功，但浏览器未能保存密钥", "error");
    }
    if (!silent) {
      toast(`已读取 ${result.models.length} 个可用模型`);
    }
    updateComposerModelPicker();
    syncComposerControls();
    return true;
  } catch (error) {
    resetModelSelect(selectSelector, "模型读取失败，请重试");
    if (!silent) showRequestFailure(error, "模型读取失败");
    else setRequestStatus({ status: "error", title: "模型自动读取失败", message: error?.message || "请检查 API Key 后重试。", code: error?.code || "MODEL_AUTOLOAD_FAILED" });
    updateComposerModelPicker();
    return false;
  } finally {
    if (button) {
      button.disabled = false;
      button.dataset.loading = "false";
      button.textContent = previousLabel === "读取中" ? "读取模型" : previousLabel;
    }
    syncComposerControls();
  }
}

function isComposerPrepControl(element) {
  if (!element) return false;
  if (element.id === "composerModelTrigger") return true;
  if (element.matches?.("[data-composer-model-settings]")) return true;
  if (element.id === "closeGenerationSettings" || element.id === "confirmGenerationSettings") return true;
  if (element.id === "loadImageModels" || element.id === "loadJudgeModels" || element.id === "clearSavedKeys") return true;
  if (element.name === "image_provider") return true;
  if (element.id === "imageKey" || element.id === "judgeKey" || element.id === "imageModel" || element.id === "judgeModel") return true;
  return Boolean(element.closest?.("#generationSettingsDialog"));
}

function canSendEntry(phase = contestPhase()) {
  if (phase !== "live") return false;
  if (state.submissionQuota?.remaining <= 0) return false;
  if ($("#submitButton")?.dataset.loading === "true") return false;
  if (!generationSettingsReady()) return false;
  return true;
}

function syncComposerControls(phase = contestPhase()) {
  const form = $("#entryForm");
  if (!form) return;
  const live = phase === "live";
  const sendEnabled = canSendEntry(phase);
  [...form.elements].forEach((element) => {
    if (updateGenerationSettingsControl(element)) return;

    // Settings + model picker stay available so users can prepare keys/models
    // before start, and still inspect/switch models after the contest ends.
    if (isComposerPrepControl(element)) {
      if (element.id === "imageModel" || element.id === "judgeModel") {
        element.disabled = element.dataset.ready !== "true" || element.dataset.loading === "true";
      } else {
        element.disabled = element.dataset.loading === "true";
      }
      return;
    }

    const requestInProgress = element.dataset.loading === "true";
    if (element.id === "submitButton") {
      // Keep disabled while loading so the stop-state icon can show, but only
      // enable for send when settings + required input are ready.
      element.disabled = !sendEnabled || requestInProgress;
      if (requestInProgress) element.disabled = true;
      return;
    }
    if (element.id === "promptAdjustment") {
      element.disabled = !live || !state.contest?.allow_prompt_adjustment || requestInProgress;
      return;
    }
    element.disabled = !live || requestInProgress;
  });
}

function updateClock() {
  const phase = contestPhase();
  const badge = $("#phaseBadge");
  badge.className = `phase ${phase}`;
  let target;
  if (phase === "upcoming") {
    badge.textContent = "未开始";
    $("#timeLabel").textContent = "距离开始";
    target = new Date(state.contest.starts_at).getTime();
  } else if (phase === "live") {
    badge.textContent = "进行中";
    $("#timeLabel").textContent = "距离截止";
    target = new Date(state.contest.ends_at).getTime();
  } else {
    badge.textContent = "已结束";
    $("#timeLabel").textContent = "比赛已截止";
    $("#countdown").textContent = "00:00:00";
    syncComposerControls(phase);
    if (!state.winnerReviewChecked && state.leaderboard.length) {
      state.winnerReviewChecked = true;
      updateWinnerReview();
    }
    return;
  }
  syncComposerControls(phase);
  const remaining = Math.max(0, target - Date.now());
  const hours = Math.floor(remaining / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  $("#countdown").textContent = [hours, minutes, seconds].map((value) => String(value).padStart(2, "0")).join(":");
}

function renderScore(submission) {
  if (!submission) return;
  $(".canvas-score-peek").hidden = false;
  $("#scorePeekValue").textContent = Number(submission.total_score) || 0;
  $("#scoreResult").innerHTML = `
    ${scoreItem("提示词", submission.prompt_match, 40)}
    ${scoreItem("构图", submission.composition, 25)}
    ${scoreItem("细节", submission.quality, 20)}
    ${scoreItem("创意", submission.creativity, 10)}
    ${scoreItem("瑕疵", submission.artifacts, 5)}
    <div class="score-total"><span>总分</span><strong>${Number(submission.total_score) || 0}</strong></div>
    <p class="judge-comment">${escapeHTML(submission.judge_comment || "评分完成")}</p>`;
}

function isCanvasScoreOpen() {
  const popover = $("#canvasScorePopover");
  return Boolean(popover && (popover.matches(":popover-open") || popover.classList.contains("is-open")));
}

let canvasScoreCloseTimer = 0;
let canvasScoreAnimationTimer = 0;

function cancelCanvasScoreClose() {
  if (!canvasScoreCloseTimer) return;
  window.clearTimeout(canvasScoreCloseTimer);
  canvasScoreCloseTimer = 0;
}

function scheduleCanvasScoreClose() {
  cancelCanvasScoreClose();
  canvasScoreCloseTimer = window.setTimeout(() => {
    canvasScoreCloseTimer = 0;
    const trigger = $("#canvasScoreTrigger");
    const popover = $("#canvasScorePopover");
    if (trigger?.matches(":hover") || popover?.matches(":hover")) return;
    closeCanvasScore();
  }, 180);
}

function positionCanvasScorePopover() {
  const trigger = $("#canvasScoreTrigger");
  const popover = $("#canvasScorePopover");
  if (!trigger || !popover || !isCanvasScoreOpen()) return;
  const triggerRect = trigger.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  const gutter = 12;
  const left = Math.min(
    window.innerWidth - popoverRect.width - gutter,
    Math.max(gutter, triggerRect.left + (triggerRect.width - popoverRect.width) / 2),
  );
  const roomBelow = window.innerHeight - triggerRect.bottom - gutter;
  const preferredTop = roomBelow >= popoverRect.height + 8
    ? triggerRect.bottom + 8
    : triggerRect.top - popoverRect.height - 8;
  popover.style.left = `${Math.round(left)}px`;
  popover.style.top = `${Math.round(Math.max(gutter, preferredTop))}px`;
  popover.style.setProperty('--score-popover-origin-x', `${Math.round(triggerRect.left + triggerRect.width / 2 - left)}px`);
  popover.style.setProperty('--score-popover-origin-y', preferredTop > triggerRect.top ? '0px' : '100%');
}

function openCanvasScore() {
  const trigger = $("#canvasScoreTrigger");
  const popover = $("#canvasScorePopover");
  if (!trigger || !popover || !popover.querySelector(".score-result:not(:empty)")) return;
  cancelCanvasScoreClose();
  if (canvasScoreAnimationTimer) window.clearTimeout(canvasScoreAnimationTimer);
  canvasScoreAnimationTimer = 0;
  popover.classList.remove('is-closing');
  popover.classList.add('is-entering');
  if (!isCanvasScoreOpen()) {
    if (typeof popover.showPopover === "function") popover.showPopover();
    else popover.classList.add("is-open");
  }
  trigger.setAttribute("aria-expanded", "true");
  requestAnimationFrame(() => {
    positionCanvasScorePopover();
    requestAnimationFrame(() => popover.classList.remove('is-entering'));
  });
}

function closeCanvasScore({ restoreFocus = false } = {}) {
  const trigger = $("#canvasScoreTrigger");
  const popover = $("#canvasScorePopover");
  cancelCanvasScoreClose();
  if (!popover || !isCanvasScoreOpen()) return;
  trigger?.setAttribute("aria-expanded", "false");
  const hide = () => {
    canvasScoreAnimationTimer = 0;
    popover.classList.remove('is-closing', 'is-entering');
    if (typeof popover.hidePopover === "function") popover.hidePopover();
    else popover.classList.remove("is-open");
  };
  if (prefersReducedMotion()) hide();
  else {
    popover.classList.add('is-closing');
    canvasScoreAnimationTimer = window.setTimeout(hide, 140);
  }
  if (restoreFocus) trigger?.focus();
}

function scoreItem(label, score, max) {
  return `<div class="score-item"><small>${label}<br>/${max}</small><strong>${Number(score) || 0}</strong></div>`;
}

function setGeneratedImageSource(dataURL) {
  state.imageDataURL = dataURL;
  setSaveStatus();
  $("#resultPanel").classList.remove("is-empty");
  $("#resultEmpty").hidden = true;
  $("#resultContent").hidden = false;
  $("#generatedImage").src = dataURL;
  $("#downloadImage").href = dataURL;
  $("#downloadImage").download = `c2p-arena-${state.submissionId || "entry"}.${dataURL.startsWith("data:image/jpeg") ? "jpg" : "png"}`;
}

function showGeneratedImagePending(dataURL) {
  setGeneratedImageSource(dataURL);
  $(".canvas-score-peek").hidden = false;
  $("#retryForm").hidden = true;
  $("#scorePeekValue").textContent = "评分中";
  $("#scoreResult").innerHTML = '<div class="score-pending"><strong>原图已保存</strong><span>正在评分…</span></div>';
  toast("原图已生成并保存到浏览器，正在评分");
}

function showGeneratedImage(dataURL, submission, scoringFailed = false) {
  setGeneratedImageSource(dataURL);
  $("#retryForm").hidden = !scoringFailed;
  if (scoringFailed) {
    $("#retryJudgeKey").value = $("#retryJudgeKey").value || $("#judgeKey").value;
    if (state.judgeModels.length && state.judgeModelsKey === $("#retryJudgeKey").value.trim()) {
      setModelOptions("#retryJudgeModel", state.judgeModels, submission?.judge_model || $("#judgeModel").value);
    }
  }
  if (submission) renderScore(submission);
  else {
    closeCanvasScore();
    $(".canvas-score-peek").hidden = true;
    $("#scorePeekValue").textContent = "--";
    $("#scoreResult").innerHTML = "";
  }
  toast(scoringFailed ? "原图已生成并保存在浏览器，评分失败未占用提交次数" : "参赛完成，请立即下载并保存原图");
}

async function submitEntry(event) {
  event.preventDefault();
  if (!ensureGenerationSettingsReady()) return;
  const button = $("#submitButton");
  button.disabled = true;
  button.dataset.loading = "true";
  button.title = "正在生成与评分";
  const sendLabel = button.querySelector(".sr-only");
  if (sendLabel) sendLabel.textContent = "正在生成与评分";
  button.setAttribute("aria-label", "正在生成与评分");
  const payload = {
    image_key: $("#imageKey").value.trim(),
    image_provider: state.imageProvider,
    judge_key: $("#judgeKey").value.trim(),
    image_model: $("#imageModel").value,
    judge_model: $("#judgeModel").value,
    prompt_adjustment: state.contest?.allow_prompt_adjustment ? $("#promptAdjustment").value.trim() : "",
  };
  setRequestStatus({ status: "running", title: "正在生成并评分", message: "生成与评分需要一点时间，请保持当前页面打开。" });
  let streamedImage = null;
  try {
    const result = await streamSubmission({ method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }, async (progress) => {
      streamedImage = progress;
      state.submissionId = progress.submission_id;
      writeBrowserStorage(`arena-submission-${state.contest.id}`, state.submissionId);
      showGeneratedImagePending(progress.image_data_url);
      try { await saveLocalEntry(progress.image_data_url, null, true, payload); } catch { toast("原图已显示，但浏览器本地保存失败，请立即下载", "error"); }
      if (progress.submission_quota) updateSubmissionQuota(progress.submission_quota);
      setRequestStatus({ status: "running", title: "图片已生成，正在评分", message: "原图已暂存，评分还在继续。若评分未完成，可稍后重新评分。", submissionId: state.submissionId });
    });
    state.submissionId = result.submission_id;
    writeBrowserStorage(`arena-submission-${state.contest.id}`, state.submissionId);
    showGeneratedImage(result.image_data_url, result.submission, false);
    try { await saveLocalEntry(result.image_data_url, result.submission, false, payload); } catch { toast("浏览器本地保存失败，请立即下载原图", "error"); }
    if (result.submission_quota) updateSubmissionQuota(result.submission_quota);
    setRequestStatus({ status: "success", title: "参赛完成", message: "图片已生成并完成评分，请立即下载保存原图。", submissionId: state.submissionId });
    await Promise.all([loadLeaderboard(), loadArchive()]);
  } catch (error) {
    if (streamedImage && !error.data?.image_data_url) error.data = { ...(error.data || {}), ...streamedImage };
    if (error.data?.image_data_url) {
      state.submissionId = error.data.submission_id;
      writeBrowserStorage(`arena-submission-${state.contest.id}`, state.submissionId);
      showGeneratedImage(error.data.image_data_url, null, true);
      try { await saveLocalEntry(error.data.image_data_url, null, true, payload); } catch { toast("浏览器本地保存失败，请立即下载原图", "error"); }
      if (error.data.submission_quota) updateSubmissionQuota(error.data.submission_quota);
      $("#retryJudgeKey").value = payload.judge_key;
      showRequestFailure(error, "图片已生成，但评分失败", state.submissionId);
    } else {
      showRequestFailure(error, "生成或评分失败");
      if (["STREAM_INTERRUPTED", "STREAM_ENDED"].includes(error?.code)) {
        void recoverContestAfterStreamFailure();
      }
    }
  } finally {
    button.dataset.loading = "false";
    button.title = "发送生成与评分";
    const sendLabel = button.querySelector(".sr-only");
    if (sendLabel) sendLabel.textContent = "发送";
    button.setAttribute("aria-label", "发送生成与评分");
    // Re-apply phase/quota disable rules without leaving a stuck disabled loading style.
    syncComposerControls();
  }
}

async function retryScore(event) {
  event.preventDefault();
  const file = $("#retryImage").files[0];
  const key = $("#retryJudgeKey").value.trim();
  const model = $("#retryJudgeModel").value;
  if ((!file && !state.imageDataURL) || !key || !model) {
    const error = new Error("请填写评分 Key、读取并选择评分模型，并保留同一原图");
    error.code = "MISSING_RETRY_INPUT";
    showRequestFailure(error, "无法重试评分", state.submissionId);
    return;
  }
  const form = new FormData();
  form.append("judge_key", key);
  form.append("judge_model", model);
  form.append("image", file || dataURLFile(state.imageDataURL, state.submissionId));
  setRequestStatus({ status: "running", title: "正在重试评分", message: "正在校验原图并请求评分模型。", submissionId: state.submissionId });
  try {
    const result = await api(`/api/submissions/${state.submissionId}/score`, { method: "POST", body: form });
    const submission = result.submission;
    renderScore(submission);
    try { await saveLocalEntry(state.imageDataURL, submission, false); } catch { toast("浏览器本地评分结果保存失败", "error"); }
    $("#retryForm").hidden = true;
    if (result.submission_quota) updateSubmissionQuota(result.submission_quota);
    setRequestStatus({ status: "success", title: "评分完成", message: "评分结果已进入实时排行。", submissionId: state.submissionId });
    toast("评分完成");
    await loadLeaderboard();
  } catch (error) {
    if (error.data?.submission_quota) updateSubmissionQuota(error.data.submission_quota);
    showRequestFailure(error, "重试评分失败", state.submissionId);
  }
}

function dataURLFile(dataURL, submissionId) {
  const [header, encoded] = String(dataURL).split(",", 2);
  const mediaType = header.match(/^data:([^;]+);base64$/)?.[1] || "image/png";
  const binary = atob(encoded || "");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  const extension = mediaType === "image/jpeg" ? "jpg" : mediaType === "image/webp" ? "webp" : mediaType === "image/gif" ? "gif" : "png";
  return new File([bytes], `${submissionId || "entry"}.${extension}`, { type: mediaType });
}

function renderLeaderboardContestOptions() {
  const select = $("#leaderboardContest");
  if (!state.leaderboardContests.length) {
    select.innerHTML = '<option value="">暂无可查看比赛</option>';
    select.disabled = true;
    $("#leaderboardContestMeta").textContent = "比赛发布后将在这里显示";
    return;
  }
  select.disabled = false;
  select.innerHTML = state.leaderboardContests.map((contest) => {
    const ended = contestPhaseFor(contest) === "ended";
    const suffix = ended ? `${new Date(contest.ends_at).toLocaleDateString()} · 已结束` : "进行中";
    return `<option value="${contest.id}">${escapeHTML(contest.title)} · ${escapeHTML(suffix)}</option>`;
  }).join("");
  select.value = String(state.leaderboardContestID || state.leaderboardContests[0].id);
  const selected = state.leaderboardContests.find((contest) => contest.id === Number(select.value));
  if (!selected) return;
  const ended = contestPhaseFor(selected) === "ended";
  $("#leaderboardContestMeta").textContent = ended
    ? `排名已锁定 · ${new Date(selected.ends_at).toLocaleString()}`
    : selected.realtime_leaderboard ? "比赛进行中 · 实时公开分数" : "比赛进行中 · 截止后揭晓分数";
}

async function loadLeaderboardContests() {
  state.leaderboardContests = await api("/api/leaderboard/contests");
  const preferredID = state.leaderboardContestID || state.contest?.id || 0;
  const selected = state.leaderboardContests.find((contest) => contest.id === preferredID) || state.leaderboardContests[0] || null;
  state.leaderboardContest = selected;
  state.leaderboardContestID = selected?.id || 0;
  renderLeaderboardContestOptions();
}

function renderLeaderboardSkeleton() {
  $("#view-leaderboard").classList.add("is-loading");
  $("#leaderboardCount").textContent = "";
  $("#leaderboardPodium").hidden = true;
  $("#leaderboardPodium").innerHTML = "";
  $("#leaderboardPagination").hidden = true;
  $("#rankingList").innerHTML = Array.from({ length: 6 }, (_, index) => `
    <div class="ranking-row ranking-skeleton-row" aria-hidden="true">
      <span></span><span></span><span></span><span></span>
    </div>`).join("");
}

function renderLeaderboardLoadError() {
  $("#view-leaderboard").classList.remove("is-loading");
  $("#leaderboardCount").textContent = "榜单读取失败";
  $("#leaderboardPodium").hidden = true;
  $("#leaderboardPodium").innerHTML = "";
  $("#rankingList").innerHTML = emptyState("榜单读取失败", "请稍后刷新", "board-empty leaderboard-empty");
  $("#leaderboardPagination").hidden = true;
}

async function loadLeaderboard({ contestID = 0, resetPage = false } = {}) {
  renderLeaderboardSkeleton();
  try {
    if (!state.leaderboardContests.length) await loadLeaderboardContests();
    const requestedID = Number(contestID) || state.leaderboardContestID || state.contest?.id || 0;
    state.leaderboardContest = state.leaderboardContests.find((contest) => contest.id === requestedID) || state.leaderboardContests[0] || null;
    state.leaderboardContestID = state.leaderboardContest?.id || 0;
    renderLeaderboardContestOptions();
    state.leaderboard = state.leaderboardContestID ? await api(`/api/leaderboard?contest_id=${state.leaderboardContestID}`) : [];
    if (resetPage) state.leaderboardPage = 1;
    state.leaderboardPage = Math.min(state.leaderboardPage, Math.max(1, Math.ceil(state.leaderboard.length / state.leaderboardPageSize)));
    renderLeaderboard();
    if (state.leaderboardContestID === state.contest?.id) {
      state.winnerReviewChecked = true;
      updateWinnerReview();
    }
  } catch (error) {
    renderLeaderboardLoadError();
    toast(error.message, "error");
  }
}

async function refreshLeaderboard() {
  await withRefreshState($("#refreshBoard"), loadLeaderboard);
}

async function loadArchive({ resetPage = true } = {}) {
  try {
    state.archive = await api("/api/archive");
    if (resetPage) state.archivePage = 1;
    state.archivePage = Math.min(state.archivePage, Math.max(1, Math.ceil(state.archive.filter((entry) => entry.winners?.length).length / state.archivePageSize)));
    renderArchive();
  } catch (error) { toast(error.message, "error"); }
}

async function refreshArchive() {
  await withRefreshState($("#refreshArchive"), () => loadArchive({ resetPage: false }));
}

async function loadRollingAnnouncement() {
  try {
    const config = await api("/api/rolling-announcement", { cache: "no-store" });
    window.ArenaRollingAnnouncement?.render($("#rollingAnnouncement"), config);
  } catch {
    $("#rollingAnnouncement").hidden = true;
  }
}

function renderArchive() {
  const container = $("#archiveList");
  const entries = state.archive.filter((entry) => entry.winners?.length);
  const pagination = $("#archivePagination");
  if (!entries.length) {
    container.innerHTML = emptyState("暂无公开获奖作品", "获奖原图审核通过后将在这里展示");
    pagination.hidden = true;
    return;
  }
  const start = (state.archivePage - 1) * state.archivePageSize;
  container.innerHTML = entries.slice(start, start + state.archivePageSize).map((entry) => `
    <article class="archive-edition">
      <header><div><small>${new Date(entry.contest.ends_at).toLocaleDateString()}</small><h3>${escapeHTML(entry.contest.title)}</h3></div><span>${entry.winners.length} 幅公开作品</span></header>
      <div class="archive-winners">${entry.winners.map((winner) => `
        <figure class="archive-work ${podiumClass(winner.rank)}">
          <button class="archive-preview-button" type="button" data-archive-image="${escapeHTML(winner.image_url)}" data-archive-alt="${escapeHTML(entry.contest.title)} 第 ${winner.rank} 名公开作品" aria-label="查看${escapeHTML(entry.contest.title)}第 ${winner.rank} 名原图"><img src="${escapeHTML(winner.image_url)}" alt="${escapeHTML(entry.contest.title)} 第 ${winner.rank} 名公开作品" loading="lazy" decoding="async"><span aria-hidden="true">查看原图</span></button>
          <figcaption><button class="archive-career-trigger" type="button" data-archive-career="${escapeHTML(winner.id)}" aria-label="查看 ${escapeHTML(winner.display_name)} 的创作者生涯"><b>#${winner.rank}</b><span>${escapeHTML(winner.display_name)}</span></button><span>${winner.total_score} / 100</span></figcaption>
        </figure>`).join("")}</div>
    </article>`).join("");
  container.querySelectorAll("[data-archive-image]").forEach((button) => button.addEventListener("click", () => openArchiveLightbox(button.dataset.archiveImage, button.dataset.archiveAlt)));
  container.querySelectorAll("[data-archive-career]").forEach((button) => button.addEventListener("click", () => openPlayerCareer(button.dataset.archiveCareer)));
  renderPagination(pagination, entries.length, state.archivePage, state.archivePageSize, (page) => { state.archivePage = page; renderArchive(); window.scrollTo({ top: $("#view-archive").offsetTop, behavior: navigationScrollBehavior() }); });
}

function isArchiveLightboxOpen() {
  const lightbox = $("#archiveLightbox");
  return Boolean(lightbox && (lightbox.open || !lightbox.hidden));
}

function clampImageViewerPosition() {
  const stage = $("#archiveViewerStage");
  const image = $("#archivePreviewImage");
  if (!stage || !image) return;
  const maxX = Math.max(0, (image.offsetWidth * imageViewer.scale - stage.clientWidth) / 2) + 28;
  const maxY = Math.max(0, (image.offsetHeight * imageViewer.scale - stage.clientHeight) / 2) + 28;
  imageViewer.x = Math.min(maxX, Math.max(-maxX, imageViewer.x));
  imageViewer.y = Math.min(maxY, Math.max(-maxY, imageViewer.y));
}

function renderImageViewer() {
  const image = $("#archivePreviewImage");
  const stage = $("#archiveViewerStage");
  if (!image || !stage) return;
  clampImageViewerPosition();
  image.style.transform = `translate(-50%, -50%) translate(${Math.round(imageViewer.x)}px, ${Math.round(imageViewer.y)}px) scale(${imageViewer.scale})`;
  stage.classList.toggle("can-pan", imageViewer.scale > 1.01);
  $("#archiveImageZoom").textContent = `${Math.round(imageViewer.scale * 100)}%`;
  $("#zoomOutArchiveImage").disabled = imageViewer.scale <= 1.01;
  $("#zoomInArchiveImage").disabled = imageViewer.scale >= 5;
}

function resetImageViewer() {
  imageViewer.scale = 1;
  imageViewer.x = 0;
  imageViewer.y = 0;
  renderImageViewer();
}

function setImageViewerScale(nextScale) {
  imageViewer.scale = Math.min(5, Math.max(1, nextScale));
  if (imageViewer.scale <= 1.01) {
    imageViewer.x = 0;
    imageViewer.y = 0;
  }
  renderImageViewer();
}

function openArchiveLightbox(url, alt) {
  const lightbox = $("#archiveLightbox");
  const image = $("#archivePreviewImage");
  if (!lightbox || !image || !url) return;
  closeCanvasScore();
  resetImageViewer();
  image.src = url;
  image.alt = alt || "获奖作品原图";
  // Native dialog top-layer keeps the preview above other modals (e.g. local detail).
  if (typeof lightbox.showModal === "function") {
    if (!lightbox.open) lightbox.showModal();
  } else {
    lightbox.hidden = false;
    document.body.style.overflow = "hidden";
  }
  $("#closeArchiveLightbox")?.focus();
}

function closeArchiveLightbox() {
  const lightbox = $("#archiveLightbox");
  if (!lightbox) return;
  if (typeof lightbox.close === "function" && lightbox.open) lightbox.close();
  else {
    lightbox.hidden = true;
    document.body.style.overflow = "";
  }
  $("#archivePreviewImage")?.removeAttribute("src");
  resetImageViewer();
}

function scoreDetailBreakdown(item) {
  const scores = [
    ["提示词", item.prompt_match, 40],
    ["构图", item.composition, 25],
    ["细节", item.quality, 20],
    ["创意", item.creativity, 10],
    ["瑕疵控制", item.artifacts, 5],
  ];
  return scores.map(([label, score, max]) => `<span><small>${label}</small><strong>${Number(score) || 0}<i>/${max}</i></strong></span>`).join("");
}

function openLocalDetail(entry) {
  if (!entry) return;
  const contest = entry.contest || { id: entry.contest_id, title: `比赛 #${entry.contest_id}`, prompt: entry.prompt || "旧版本本地记录" };
  const submission = entry.submission;
  const scored = submission?.status === "scored";
  const imageProvider = localEntryImageProvider(entry).toUpperCase();
  const imageModel = localEntryImageModel(entry) || "-";
  const judgeModel = submission?.judge_model || entry.judge_model || "-";
  const officialPrompt = submission?.official_prompt_snapshot || contest.prompt || entry.prompt || "-";
  const adjustment = submission?.prompt_adjustment || entry.prompt_adjustment || "";
  const scoreHead = scored
    ? `${Number(submission.total_score) || 0} / 100 · ${escapeHTML(localAttemptLabel(submission))}`
    : escapeHTML(localAttemptLabel(submission));
  const savedAt = new Date(submission?.created_at || entry.saved_at).toLocaleString();
  const scoreSection = scored
    ? `<section aria-labelledby="localScoreBreakdownTitle"><h3 id="localScoreBreakdownTitle">各项评分</h3><div class="score-detail-breakdown">${scoreDetailBreakdown(submission)}</div></section>
       ${submission?.judge_comment ? `<section aria-labelledby="localJudgeCommentTitle"><h3 id="localJudgeCommentTitle">裁判评语</h3><p class="score-detail-copy">${escapeHTML(submission.judge_comment)}</p></section>` : ""}`
    : `<section aria-labelledby="localScorePendingTitle"><h3 id="localScorePendingTitle">评分状态</h3><p class="score-detail-copy">评分尚未完成。可返回比赛页继续评分，或下载原图备份。</p></section>`;
  const content = $("#scoreDetailContent");
  content.innerHTML = `<header class="score-detail-head"><div><small>${scoreHead}</small><h2 id="scoreDetailTitle">本地作品详情</h2><p title="${escapeHTML(contest.title)}">${escapeHTML(contest.title)} · ${escapeHTML(savedAt)}</p></div><button type="button" data-score-detail-close aria-label="关闭本地作品详情">×</button></header>
    <div class="score-detail-body">
      <section class="local-detail-preview-section" aria-label="作品预览">
        <button type="button" class="local-detail-preview" data-local-detail-preview aria-label="预览原图">
          <img src="${entry.image_data_url}" alt="${escapeHTML(contest.title)} 本地参赛作品">
        </button>
      </section>
      ${scoreSection}
      <section aria-labelledby="localPromptTitle"><h3 id="localPromptTitle">提示词</h3>
        <div class="score-detail-prompts ${adjustment ? "" : "is-single"}">
          <div><h4>官方提示词</h4><p>${escapeHTML(officialPrompt)}</p></div>
          ${adjustment ? `<div><h4>我的微调</h4><p>${escapeHTML(adjustment)}</p></div>` : ""}
        </div>
      </section>
      <section aria-labelledby="localMetaTitle"><h3 id="localMetaTitle">提交信息</h3>
        <dl class="local-detail-meta">
          <div><dt>生图</dt><dd><span class="local-detail-meta-main">${escapeHTML(imageProvider)}</span><span class="local-detail-meta-sub">${escapeHTML(imageModel)}</span></dd></div>
          <div><dt>评分</dt><dd><span class="local-detail-meta-main">${escapeHTML(judgeModel)}</span></dd></div>
          <div><dt>比赛</dt><dd><span class="local-detail-meta-main">#${escapeHTML(String(contest.id || entry.contest_id || "-"))}</span><span class="local-detail-meta-sub" title="${escapeHTML(contest.title)}">${escapeHTML(contest.title)}</span></dd></div>
          <div><dt>编号</dt><dd><code>${escapeHTML(entry.submission_id)}</code></dd></div>
          <div><dt>时间</dt><dd><span class="local-detail-meta-main">${escapeHTML(savedAt)}</span></dd></div>
        </dl>
      </section>
    </div>`;
  content.querySelector("[data-score-detail-close]").addEventListener("click", () => $("#scoreDetailDialog").close());
  content.querySelector("[data-local-detail-preview]")?.addEventListener("click", () => {
    if (entry.image_data_url) openArchiveLightbox(entry.image_data_url, `${localEntryContestTitle(entry)} 本地作品`);
  });
  $("#scoreDetailDialog").showModal();
}

function openScoreDetail(item, contest) {
  const contestEnded = contestPhaseFor(contest) === "ended";
  const winnerCount = Number(contest?.winner_count) || 3;
  const showComment = contestEnded && Number(item.rank) <= 3 && item.judge_comment;
  const showCreative = contestEnded && Number(item.rank) <= winnerCount && item.prompt_adjustment;
  const content = $("#scoreDetailContent");
  content.innerHTML = `<header class="score-detail-head"><div><small>第 ${Number(item.rank)} 名 · ${Number(item.total_score) || 0} / 100</small><h2 id="scoreDetailTitle">评分详情</h2><p>${escapeHTML(item.display_name)} · ${escapeHTML((item.image_provider || "openai").toUpperCase())} · ${escapeHTML(item.image_model || "-")}</p></div><button type="button" data-score-detail-close aria-label="关闭评分详情">×</button></header><div class="score-detail-body"><section aria-labelledby="scoreBreakdownTitle"><h3 id="scoreBreakdownTitle">各项评分</h3><div class="score-detail-breakdown">${scoreDetailBreakdown(item)}</div></section>${showComment ? `<section aria-labelledby="judgeCommentTitle"><h3 id="judgeCommentTitle">裁判评语</h3><p class="score-detail-copy">${escapeHTML(item.judge_comment)}</p></section>` : ""}${showCreative ? `<section aria-labelledby="creativeDetailTitle"><h3 id="creativeDetailTitle">获奖创意</h3><div class="score-detail-prompts"><div><h4>官方提示词</h4><p>${escapeHTML(item.official_prompt_snapshot || contest?.prompt || "-")}</p></div><div><h4>获奖者微调</h4><p>${escapeHTML(item.prompt_adjustment)}</p></div></div></section>` : ""}</div>`;
  content.querySelector("[data-score-detail-close]").addEventListener("click", () => $("#scoreDetailDialog").close());
  $("#scoreDetailDialog").showModal();
}

function renderLeaderboard() {
  $("#view-leaderboard").classList.remove("is-loading");
  const items = state.leaderboard;
  const list = $("#rankingList");
  const contest = state.leaderboardContest || state.contest;
  const contestEnded = contestPhaseFor(contest) === "ended";
  const revealScores = Boolean(contest?.realtime_leaderboard) || contestEnded;
  const detailedRanking = contest?.ranking_policy !== "legacy";
  const winnerCount = Number(contest?.winner_count) || 3;
  const podium = $("#leaderboardPodium");
  const podiumItems = revealScores ? items.filter((item) => Number(item.rank) >= 1 && Number(item.rank) <= 3).slice(0, 3) : [];
  state.podiumRenderToken += 1;
  const podiumToken = state.podiumRenderToken;
  podium.hidden = podiumItems.length < 3;
  if (podiumItems.length === 3) {
    const contestKey = String(contest?.id || state.leaderboardContestID || 'current');
    const animateScores = !state.podiumAnimatedContestIDs.has(contestKey);
    state.podiumAnimatedContestIDs.add(contestKey);
    renderLeaderboardPodium(podiumItems, animateScores);
    loadLeaderboardPodiumCareers(podiumItems, podiumToken);
  } else {
    podium.innerHTML = "";
  }
  $("#leaderboardCount").textContent = contestEnded ? `${items.length} 位参赛者 · 最终排名已锁定` : revealScores ? `${items.length} 位已评分参赛者 · 实时排名` : `${items.length} 位已提交参赛者 · 分数待揭晓`;
  $("#view-leaderboard h2").textContent = contestEnded ? "最终排行榜" : revealScores ? "实时排行榜" : "提交顺序榜";
  const firstHeader = $(".ranking-head span");
  if (firstHeader) firstHeader.textContent = revealScores ? "名次" : "顺序";
  $("#rankingPrimaryRule").textContent = detailedRanking
    ? "揭榜后按总分从高到低排序；总分相同，依次比较提示词完成度、构图、清晰度与细节、创意和瑕疵控制，细分成绩仍相同时，成功提交时间更早的作品排名更高，作品编号仅用于极端同序时保持排名稳定。"
    : "本场沿用创建时的排名规则：揭榜后按总分从高到低排序；总分相同，成功提交时间更早的作品排名更高。";
  $("#rankingBestRule").textContent = detailedRanking
    ? "每位用户只保留按上述规则表现最好的一件作品。"
    : "每位用户只保留总分最高的一件作品；同分时保留成功提交更早的作品。";
  if (!items.length) {
    list.innerHTML = contestEnded
      ? emptyState("本场暂无有效排名", "比赛已结束，没有成功评分的参赛作品", "board-empty leaderboard-empty")
      : emptyState("等待首个提交", "成功评分后将显示在这里", "board-empty leaderboard-empty");
    $("#leaderboardPagination").hidden = true;
    return;
  }
  const start = (state.leaderboardPage - 1) * state.leaderboardPageSize;
  list.innerHTML = items.slice(start, start + state.leaderboardPageSize).map((item) => {
    const placementClass = revealScores ? podiumClass(item.rank) : "";
    return `<div class="ranking-row ${revealScores && item.rank <= winnerCount ? "top-rank" : ""} ${placementClass}"><span class="rank">${String(item.rank).padStart(2, "0")}</span><button class="name player-career-trigger" type="button" data-player-career="${escapeHTML(item.id)}" aria-label="查看 ${escapeHTML(item.display_name)} 的创作者生涯">${playerAvatarHTML(item.display_name, "", item)}<span class="player-name">${escapeHTML(item.display_name)}</span></button><code>${escapeHTML((item.image_provider || "openai").toUpperCase())} · ${escapeHTML(item.image_model)}</code><div class="ranking-points-cell">${revealScores ? `<button class="score-detail-trigger icon-score-detail-trigger" type="button" data-score-detail="${escapeHTML(item.id)}" title="评分详情" aria-label="查看评分详情"><svg class="nav-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h10M4 18h7"/><path d="m16 16 2 2 4-4"/></svg></button>` : ""}<strong class="points ${revealScores ? "" : "is-hidden"}">${revealScores ? item.total_score : "待揭晓"}</strong></div></div>`;
  }).join("");
  hydratePlayerAvatars(list);
  list.querySelectorAll("[data-player-career]").forEach((button) => button.addEventListener("click", () => openPlayerCareer(button.dataset.playerCareer)));
  list.querySelectorAll("[data-score-detail]").forEach((button) => button.addEventListener("click", () => {
    const item = items.find((entry) => String(entry.id) === button.dataset.scoreDetail);
    if (item) openScoreDetail(item, contest);
  }));
  renderPagination($("#leaderboardPagination"), items.length, state.leaderboardPage, state.leaderboardPageSize, (page) => { state.leaderboardPage = page; renderLeaderboard(); window.scrollTo({ top: $("#view-leaderboard").offsetTop, behavior: navigationScrollBehavior() }); });
}

function renderLeaderboardPodium(items, animateScores) {
  const order = { 2: 0, 1: 1, 3: 2 };
  const ordered = [...items].sort((a, b) => (order[Number(a.rank)] ?? 3) - (order[Number(b.rank)] ?? 3));
  const podium = $("#leaderboardPodium");
  podium.innerHTML = ordered.map((item) => `
    <article class="podium-card rank-${Number(item.rank)}" data-podium-player="${escapeHTML(item.id)}">
      <header><span class="podium-rank-label">#${Number(item.rank)}</span><span class="podium-medal">${Number(item.rank) === 1 ? "冠军" : Number(item.rank) === 2 ? "亚军" : "季军"}</span></header>
      <button class="podium-person" type="button" data-podium-career="${escapeHTML(item.id)}" aria-label="查看 ${escapeHTML(item.display_name)} 的创作者生涯">${playerAvatarHTML(item.display_name, "podium-avatar", item)}<span class="podium-name">${escapeHTML(item.display_name)}</span></button>
      <div class="podium-score"><strong data-count-up-target="${Number(item.total_score) || 0}">0</strong><span>总分 / 100</span></div>
      <p class="podium-signature">正在整理公开签名…</p>
      <div class="podium-career-summary"><span><b>—</b>参赛场</span><span><b>—</b>获奖次</span><span><b>—</b>历史最佳</span></div>
    </article>`).join("");
  hydratePlayerAvatars(podium);
  animatePodiumScores(podium, animateScores);
  podium.querySelectorAll("[data-podium-career]").forEach((button) => button.addEventListener("click", () => openPlayerCareer(button.dataset.podiumCareer)));
}

function animatePodiumScores(root, animateScores) {
  const scores = [...root.querySelectorAll("[data-count-up-target]")];
  if (!scores.length) return;
  if (!animateScores || prefersReducedMotion()) {
    scores.forEach((node) => { node.textContent = String(Number(node.dataset.countUpTarget) || 0); });
    return;
  }
  scores.forEach((node, index) => {
    const target = Number(node.dataset.countUpTarget) || 0;
    const duration = 220 + index * 40;
    const startedAt = performance.now();
    node.classList.add("is-counting");
    const tick = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      node.textContent = String(Math.round(target * eased));
      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        node.textContent = String(target);
        node.classList.remove("is-counting");
      }
    };
    requestAnimationFrame(tick);
  });
}

async function loadLeaderboardPodiumCareers(items, token) {
  await Promise.all(items.map(async (item) => {
    try {
      const profile = await api(`/api/players/${encodeURIComponent(item.id)}/career`);
      if (token !== state.podiumRenderToken) return;
      const card = document.querySelector(`[data-podium-player="${CSS.escape(item.id)}"]`);
      if (!card) return;
      card.querySelector(".podium-signature").textContent = profile.signature || "token带走了我的余额，但是留下了生产力的痕迹。";
      card.querySelector(".podium-career-summary").innerHTML = `<span><b>${Number(profile.contest_count) || 0}</b>参赛场</span><span><b>${Number(profile.win_count) || 0}</b>获奖次</span><span><b>${playerCareerRank(profile)}</b>历史最佳</span>`;
      applyPlayerAvatar(card.querySelector(".podium-avatar"), profile.display_name, profile);
    } catch {
      if (token !== state.podiumRenderToken) return;
      const card = document.querySelector(`[data-podium-player="${CSS.escape(item.id)}"]`);
      if (card) card.querySelector(".podium-signature").textContent = "签名暂时不可用";
    }
  }));
}

function playerCareerTitle(profile) {
  if (profile.best_rank === 1) return "冠军创作者";
  if (profile.win_count >= 3) return "常胜创作者";
  if (profile.win_count > 0) return "获奖创作者";
  if (profile.contest_count >= 5) return "资深参赛者";
  return "新锐创作者";
}

function playerCareerRank(profile) {
  return Number(profile.best_rank) > 0 ? `#${profile.best_rank}` : "—";
}

function applyPlayerAvatar(element, email, profile = {}) {
  if (!element) return;
  const avatar = playerAvatarPresentation(email, profile);
  element.textContent = avatar.label;
  element.style.background = avatar.color;
}

function playerAvatarHTML(email, className = "", profile = {}) {
  const avatar = playerAvatarPresentation(email, profile);
  return `<span class="player-avatar ${escapeHTML(className)}" data-avatar-color="${avatar.color}" aria-hidden="true">${escapeHTML(avatar.label)}</span>`;
}

function hydratePlayerAvatars(root) {
  root?.querySelectorAll("[data-avatar-color]").forEach((element) => {
    element.style.background = safeAvatarColor(element.dataset.avatarColor);
  });
}

function applyProfileBackground(element, profile) {
  if (!element) return;
  const presentation = profileBackgroundPresentation(profile);
  element.style.background = presentation.background;
  element.classList.toggle("has-dark-background", presentation.dark);
}

function updateProfilePreview() {
  if (!state.profile) return;
  const signature = $("#profileSignature").value.trim() || "token带走了我的余额，但是留下了生产力的痕迹。";
  const color = $("#profileBackgroundColor").value || "#eaf2ff";
  const selectedID = Number($("#profileRewardBackground").value || 0);
  const selected = (state.profile.reward_backgrounds || []).find((item) => Number(item.id) === selectedID) || null;
  const avatarColor = $("#profileAvatarColor").value || "#4b5563";
  const selectedAvatarID = Number($("#profileRewardAvatar").value || 0);
  const selectedAvatar = (state.profile.reward_avatars || []).find((item) => Number(item.id) === selectedAvatarID) || null;
  $("#profilePreviewSignature").textContent = signature;
  $("#profileSignatureCounter").textContent = `${Array.from($("#profileSignature").value).length} / 120`;
  $("#profileBackgroundValue").textContent = color;
  $("#profileAvatarValue").textContent = avatarColor;
  $("#profilePreviewTheme").textContent = selected?.name || "自选配色";
  $("#profilePreviewAvatar").title = selectedAvatar?.name || "邮箱头像";
  applyPlayerAvatar($("#profilePreviewAvatar"), state.profile.email, { avatar_color: avatarColor, selected_reward_avatar: selectedAvatar });
  applyProfileBackground($("#profilePreview"), { background_color: color, selected_reward_background: selected });
}

function switchProfileStack(panelName) {
  const selected = ["identity", "rewards"].includes(panelName) ? panelName : "identity";
  document.querySelectorAll("[data-profile-stack-tab]").forEach((button) => {
    button.setAttribute("aria-selected", String(button.dataset.profileStackTab === selected));
  });
  document.querySelectorAll("[data-profile-stack-panel]").forEach((panel) => {
    panel.hidden = panel.dataset.profileStackPanel !== selected;
  });
}

function renderMyProfile(profile) {
  state.profile = profile;
  $("#profilePreview").hidden = false;
  $("#profileForm").hidden = false;
  $("#profilePreviewEmail").textContent = maskedProfileEmail(profile.email);
  $("#profileSignature").value = profile.signature || "token带走了我的余额，但是留下了生产力的痕迹。";
  $("#profileAvatarColor").value = safeAvatarColor(profile.avatar_color);
  $("#profileBackgroundColor").value = profile.background_color || "#eaf2ff";
  const backgrounds = Array.isArray(profile.reward_backgrounds) ? profile.reward_backgrounds : [];
  const select = $("#profileRewardBackground");
  select.innerHTML = `<option value="0">使用自选纯色</option>${backgrounds.map((item) => `<option value="${Number(item.id)}">${escapeHTML(item.name)}</option>`).join("")}`;
  select.value = String(profile.selected_reward_background_id || 0);
  $("#profileRewardBackgroundField").hidden = backgrounds.length === 0;
  const avatars = Array.isArray(profile.reward_avatars) ? profile.reward_avatars : [];
  const avatarSelect = $("#profileRewardAvatar");
  avatarSelect.innerHTML = `<option value="0">使用邮箱头像与自选颜色</option>${avatars.map((item) => `<option value="${Number(item.id)}">${escapeHTML(item.name)} · ${escapeHTML(item.label)}</option>`).join("")}`;
  avatarSelect.value = String(profile.selected_reward_avatar_id || 0);
  $("#profileRewardAvatarField").hidden = avatars.length === 0;
  $("#profileUnlockHint").textContent = `已验证 ${maskedProfileEmail(profile.email)}，可直接编辑公开资料。`;
  const awardButton = $("#awardLookupForm button");
  if (awardButton) {
    awardButton.disabled = false;
    awardButton.textContent = "查询历史排名";
  }
  updateProfilePreview();
}

async function fetchMyProfile(key, { quiet = false } = {}) {
  if (!key || state.profileLoading) return false;
  state.profileLoading = true;
  $("#profileUnlockForm").classList.add("is-loading");
  try {
    const profile = await api("/api/profile/lookup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ api_key: key }),
    });
    state.profileKey = key;
    $("#profileKey").value = key;
    await saveLocalCredential("profile", key);
    renderMyProfile(profile);
    if (!quiet) toast("个人资料已读取");
    return true;
  } catch (error) {
    if (!quiet) {
      $("#profileUnlockHint").textContent = error.message;
      toast(error.message, "error");
    }
    return false;
  } finally {
    state.profileLoading = false;
    $("#profileUnlockForm").classList.remove("is-loading");
  }
}

async function savedProfileKeyCandidates() {
  const ids = ["profile", "judge", "image:openai", "image:gemini", "award"];
  const keys = await Promise.all(ids.map((id) => readLocalCredential(id).catch(() => "")));
  return [...new Set(keys.map((key) => key.trim()).filter(Boolean))];
}

async function loadProfileAutomatically(force = false) {
  if (state.profileLoading || state.profile) return;
  if (state.profileAutoAttempted && !force) return;
  state.profileAutoAttempted = true;
  const candidates = await savedProfileKeyCandidates();
  if (!candidates.length) {
    $("#profileUnlockHint").textContent = "当前浏览器没有已保存的 Key，请填写本人任意有效 Key。";
    return;
  }
  $("#profileUnlockHint").textContent = "正在使用当前浏览器已保存的 Key 自动验证身份…";
  for (const key of candidates) {
    if (await fetchMyProfile(key, { quiet: true })) return;
  }
  $("#profileUnlockHint").textContent = "已保存的 Key 均已失效，请填写新的有效 Key。";
}

async function submitProfileUnlock(event) {
  event.preventDefault();
  const key = $("#profileKey").value.trim();
  if (!key) {
    toast("请填写本人任意有效 API Key", "error");
    return;
  }
  await fetchMyProfile(key);
}

async function saveMyProfile(event) {
  event.preventDefault();
  if (!state.profileKey) {
    toast("请先验证 API Key", "error");
    return;
  }
  try {
    const profile = await api("/api/profile", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        api_key: state.profileKey,
        signature: $("#profileSignature").value.trim(),
        background_color: $("#profileBackgroundColor").value,
        selected_reward_background_id: Number($("#profileRewardBackground").value || 0),
        avatar_color: $("#profileAvatarColor").value,
        selected_reward_avatar_id: Number($("#profileRewardAvatar").value || 0),
      }),
    });
    renderMyProfile(profile);
    toast("个人资料已保存");
  } catch (error) {
    toast(error.message, "error");
  }
}

function renderPlayerCareer(profile) {
  const wins = Array.isArray(profile.wins) ? profile.wins : [];
  const reward = profile.reward_total_available ? `$${Number(profile.reward_total || 0).toFixed(2)}` : "暂不可用";
  const firstDate = profile.first_participated_at ? new Date(profile.first_participated_at).toLocaleDateString("zh-CN") : "-";
  const achievements = wins.length ? wins.slice(0, 6).map((win) => {
    const rewardValue = Number(win.reward_value) > 0 ? `<span class="career-award-value">+$${Number(win.reward_value).toFixed(2)}</span>` : "";
    const status = win.confirmed ? "已确认" : "待审核";
    return `<li class="career-award rank-${Math.min(Number(win.rank) || 4, 4)}"><span class="career-award-rank">#${Number(win.rank) || "-"}</span><div><strong>${escapeHTML(win.contest_title)}</strong><small>${escapeHTML(new Date(win.awarded_at).toLocaleDateString("zh-CN"))} · ${status}</small></div>${rewardValue}</li>`;
  }).join("") : `<li class="career-award-empty"><strong>荣誉正在积累</strong><span>完成更多比赛，向第一个获奖名次发起冲击。</span></li>`;
  $("#playerCareerContent").innerHTML = `
    <article class="player-career-card">
      <header class="player-career-head">
        ${playerAvatarHTML(profile.display_name, "player-career-mark", profile)}
        <div><small>创作者生涯</small><h2 id="playerCareerTitle">${escapeHTML(profile.display_name)}</h2><p class="player-career-signature">${escapeHTML(profile.signature || "token带走了我的余额，但是留下了生产力的痕迹。")}</p><span class="player-career-meta">${escapeHTML(playerCareerTitle(profile))} · ${firstDate} 首次参赛</span></div>
        <span class="player-career-badge">${escapeHTML(playerCareerTitle(profile))}</span>
      </header>
      <div class="player-career-stats">
        <div><small>参赛次数</small><strong>${Number(profile.contest_count) || 0}</strong><span>场比赛</span></div>
        <div><small>提交评分</small><strong>${Number(profile.scored_submission_count) || 0}</strong><span>次成功</span></div>
        <div><small>历史最佳</small><strong>${playerCareerRank(profile)}</strong><span>最终名次</span></div>
        <div><small>获奖次数</small><strong>${Number(profile.win_count) || 0}</strong><span>次入围</span></div>
        <div class="career-reward-stat"><small>累计奖金</small><strong>${escapeHTML(reward)}</strong><span>审核通过的余额奖励</span></div>
      </div>
      <section class="player-career-awards"><header><h3>生涯荣誉</h3><span>${wins.length ? `最近 ${Math.min(wins.length, 6)} 条` : "等待首次上榜"}</span></header><ol>${achievements}</ol></section>
      <footer>数据来自「每日一图」公开比赛记录，邮箱始终脱敏展示。</footer>
    </article>`;
  hydratePlayerAvatars($("#playerCareerContent"));
  applyProfileBackground($(".player-career-head"), profile);
}

async function openPlayerCareer(submissionID) {
  const dialog = $("#playerCareerDialog");
  $("#playerCareerContent").innerHTML = `<div class="player-career-loading"><span aria-hidden="true"></span><strong>正在整理创作者生涯</strong></div>`;
  if (!dialog.open) dialog.showModal();
  try {
    const profile = await api(`/api/players/${encodeURIComponent(submissionID)}/career`, { cache: "no-store" });
    renderPlayerCareer(profile);
  } catch (error) {
    $("#playerCareerContent").innerHTML = `<div class="player-career-loading is-error"><strong>暂时无法读取生涯数据</strong><span>${escapeHTML(error.message)}</span></div>`;
  }
}

function closePlayerCareer() {
  const dialog = $("#playerCareerDialog");
  if (dialog.open) dialog.close();
}

async function updateWinnerReview() {
  const panel = $("#winnerReview");
  if (contestPhase() !== "ended" || !state.contest) { panel.hidden = true; return; }
  let localEntries = [];
  try {
    localEntries = (await readLocalEntriesForContest(state.contest.id)).filter((item) => item.image_data_url);
  } catch {
    panel.hidden = true;
    return;
  }
  const winnerCount = Number(state.contest.winner_count) || 3;
  const localByID = new Map(localEntries.map((item) => [item.submission_id, item]));
  const entry = state.leaderboard.find((item) => item.rank <= winnerCount && localByID.has(item.id));
  if (entry && entry.id !== state.submissionId) {
    const saved = localByID.get(entry.id);
    state.submissionId = entry.id;
    writeBrowserStorage(`arena-submission-${state.contest.id}`, entry.id);
    showGeneratedImage(saved.image_data_url, saved.submission, saved.scoring_failed);
    setSaveStatus();
  }
  if (!entry || entry.review_status === "approved" || entry.review_status === "waived") { panel.hidden = true; return; }
  panel.hidden = false;
  $("#winnerRank").textContent = entry.rank;
  const form = $("#reviewForm");
  const pending = entry.review_status === "pending";
  [...form.elements].forEach((element) => { element.disabled = pending; });
  if (pending) toast("获奖原图已经提交，正在等待管理员审核");
}

async function submitReview(event) {
  event.preventDefault();
  const file = $("#reviewImage").files[0];
  const key = $("#reviewKey").value.trim();
  if (!file || !key) {
    const error = new Error("请填写本人 API Key 并选择参赛原图");
    error.code = "MISSING_REVIEW_INPUT";
    showRequestFailure(error, "无法提交获奖审核", state.submissionId);
    return;
  }
  const form = new FormData();
  form.append("api_key", key);
  form.append("image", file);
  form.append("publish", $("#publishChoice").checked ? "true" : "false");
  setRequestStatus({ status: "running", title: "正在提交获奖审核", message: "正在校验原图和 API Key 归属。", submissionId: state.submissionId });
  try {
    await api(`/api/submissions/${state.submissionId}/review`, { method: "POST", body: form });
    setRequestStatus({ status: "success", title: "原图已提交审核", message: "管理员审核结果会反映在排行榜和往期作品中。", submissionId: state.submissionId });
    toast("原图已提交审核");
    await Promise.all([loadLeaderboard(), loadArchive()]);
    await renderLocalSubmissions();
  } catch (error) { showRequestFailure(error, "获奖审核提交失败", state.submissionId); }
}

function awardReviewLabel(status) {
  return ({ not_submitted: "待提交原图", pending: "审核中", approved: "审核通过", rejected: "审核未通过", waived: "免原图发奖" })[status] || status || "待处理";
}

function awardRewardStatusLabel(status) {
  return ({ not_winner: "本场未获奖", awaiting_review: "审核通过后可领取", not_configured: "管理员尚未配置奖励", unused: "可兑换", used: "已兑换", expired: "已过期", missing: "兑换码已失效" })[status] || status || "待处理";
}

function awardRewardValue(reward) {
  if (!reward) return "";
  if (reward.type === "balance") return `$${Number(reward.value).toFixed(2)} 余额`;
  if (reward.type === "concurrency") return `${Number(reward.value)} 并发额度`;
  if (reward.type === "subscription") return `${reward.validity_days || 0} 天订阅`;
  return `${reward.type || "兑换码"} · ${Number(reward.value) || 0}`;
}

function awardRewardHTML(record) {
  const { reward_status: status, reward } = record;
  if (!reward) {
    let message = "该名次尚无可领取的 CDK，请联系管理员。";
    if (status === "not_winner") message = `最终排名第 ${record.submission.rank} 名，未进入本场前 ${record.contest.winner_count || 3} 名获奖区。`;
    if (status === "awaiting_review") {
      message = record.submission.review_status === "pending"
        ? "原图已提交，正在等待管理员审核。"
        : "请提交参赛原图，管理员审核通过后再次使用本人 API Key 查询奖励。";
    }
    return `<div class="award-reward locked"><strong>${escapeHTML(awardRewardStatusLabel(status))}</strong><p>${escapeHTML(message)}</p></div>`;
  }
  const tracking = [];
  if (awardRewardValue(reward)) tracking.push(awardRewardValue(reward));
  if (reward.used_at) tracking.push(`使用时间 ${new Date(reward.used_at).toLocaleString()}`);
  if (reward.used_email) tracking.push(`兑换邮箱 ${reward.used_email}`);
  if (reward.expires_at && reward.status !== "used") tracking.push(`有效期至 ${new Date(reward.expires_at).toLocaleString()}`);
  return `<div class="award-reward ${escapeHTML(reward.status)}">
    <div><span>奖励兑换码</span><strong>${escapeHTML(awardRewardStatusLabel(reward.status))}</strong></div>
    <div class="award-code"><code>${escapeHTML(reward.code)}</code><button type="button" data-copy-cdk="${escapeHTML(reward.code)}">复制 CDK</button></div>
    <p>${escapeHTML(tracking.join(" · ") || "兑换状态已与 sub2api 同步")}</p>
  </div>`;
}

function awardReviewHTML(record) {
  const { contest, submission } = record;
  const isWinner = submission.rank >= 1 && submission.rank <= (contest.winner_count || 3);
  if (!isWinner || submission.review_status === "approved" || submission.review_status === "waived" || submission.review_status === "pending") return "";
  const retrying = submission.review_status === "rejected";
  return `<form class="award-review-form" data-award-review="${escapeHTML(submission.id)}">
    <div class="award-review-intro"><strong>${retrying ? "重新提交获奖原图" : "提交获奖原图"}</strong><p>原图必须与参赛时保存的文件完全一致。管理员审核通过时可将获奖作品公开展示；未选择文件时，会尝试读取当前浏览器保存的同一作品。</p></div>
    <label class="award-review-file"><span>参赛原图</span><input type="file" name="image" accept="image/png,image/jpeg,image/webp,image/gif"></label>
    <label class="award-review-publish"><input type="checkbox" name="publish"><span>审核通过后公开展示作品</span></label>
    <button type="submit">${retrying ? "重新提交审核" : "提交审核"}</button>
  </form>`;
}

async function submitAwardReview(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const submissionId = form.dataset.awardReview;
  const apiKey = state.profileKey;
  let image = form.querySelector('input[name="image"]').files[0];
  if (!image) {
    try {
      const localEntry = await readLocalEntry(submissionId);
      if (localEntry) image = dataURLFile(localEntry.image_data_url, submissionId);
    } catch {
      // The user can still select the downloaded original manually.
    }
  }
  if (!apiKey || !image) {
    const error = new Error("请选择参赛原图；若浏览器仍保存该作品，可不选文件直接提交");
    error.code = "MISSING_REVIEW_INPUT";
    showRequestFailure(error, "无法提交获奖审核", submissionId);
    return;
  }
  const button = form.querySelector('button[type="submit"]');
  button.disabled = true;
  const payload = new FormData();
  payload.append("api_key", apiKey);
  payload.append("image", image);
  payload.append("publish", form.querySelector('input[name="publish"]').checked ? "true" : "false");
  setRequestStatus({ status: "running", title: "正在提交历史获奖审核", message: "正在校验原图、最终名次和 API Key 归属。", submissionId });
  try {
    await api(`/api/submissions/${encodeURIComponent(submissionId)}/review`, { method: "POST", body: payload });
    const records = await api("/api/awards", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ api_key: apiKey }) });
    renderAwardResults(records);
    setRequestStatus({ status: "success", title: "原图已提交审核", message: "可在本页使用同一 API Key 查询最新审核状态。", submissionId });
    toast("获奖原图已提交审核");
  } catch (error) {
    showRequestFailure(error, "获奖审核提交失败", submissionId);
    if (button.isConnected) button.disabled = false;
  }
}

function renderAwardResults(records = state.awardRecords, { resetPage = false } = {}) {
  const container = $("#awardResults");
  const pagination = $("#awardPagination");
  state.awardRecords = Array.isArray(records) ? records : [];
  if (resetPage) state.awardPage = 1;
  container.hidden = false;
  if (!state.awardRecords.length) {
    container.innerHTML = emptyState("暂无获奖记录", "没有查询到已结束比赛的获奖信息", "award-empty");
    pagination.hidden = true;
    return;
  }
  state.awardPage = Math.min(state.awardPage, Math.max(1, Math.ceil(state.awardRecords.length / state.awardPageSize)));
  const start = (state.awardPage - 1) * state.awardPageSize;
  container.innerHTML = state.awardRecords.slice(start, start + state.awardPageSize).map((record) => {
    const { contest, submission } = record;
    const isWinner = submission.rank >= 1 && submission.rank <= (contest.winner_count || 3);
    const reviewNotice = !isWinner
      ? { label: "未进入获奖区", className: "not-winner" }
      : submission.review_status === "approved"
        ? { label: "审核通过", className: "approved" }
        : submission.review_status === "waived"
          ? { label: "免原图发奖", className: "approved" }
        : submission.review_status === "pending"
          ? { label: "原图审核中", className: "pending" }
          : { label: "需提交原图审核", className: "needs-review" };
    return `
    <article class="award-record">
      <div class="award-record-rank"><small>最终排名</small><strong>#${submission.rank}</strong></div>
      <div class="award-record-copy"><small>${escapeHTML(new Date(contest.ends_at).toLocaleDateString())}</small><h3>${escapeHTML(contest.title)}</h3><p>${escapeHTML(contest.prize_text || `前 ${contest.winner_count || 3} 名奖励`)}</p><span class="award-action-label ${reviewNotice.className}">${reviewNotice.label}</span></div>
      <div class="award-record-result"><span>${submission.total_score} / 100</span><small>${escapeHTML(awardReviewLabel(submission.review_status))}</small></div>
      ${submission.image_url ? `<button class="award-record-preview" type="button" data-award-image="${escapeHTML(submission.image_url)}" data-award-alt="${escapeHTML(contest.title)} 第 ${submission.rank} 名原图" aria-label="查看${escapeHTML(contest.title)}参赛原图"><img src="${escapeHTML(submission.image_url)}" alt=""></button>` : ""}
      ${awardReviewHTML(record)}
      ${awardRewardHTML(record)}
    </article>`;
  }).join("");
  container.querySelectorAll("[data-copy-cdk]").forEach((button) => button.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(button.dataset.copyCdk);
      toast("奖励 CDK 已复制");
    } catch {
      toast("复制失败，请手动选择 CDK", "error");
    }
  }));
  container.querySelectorAll("[data-award-review]").forEach((form) => form.addEventListener("submit", submitAwardReview));
  container.querySelectorAll("[data-award-image]").forEach((button) => button.addEventListener("click", () => openArchiveLightbox(button.dataset.awardImage, button.dataset.awardAlt)));
  renderPagination(pagination, state.awardRecords.length, state.awardPage, state.awardPageSize, (page) => {
    state.awardPage = page;
    renderAwardResults();
    container.scrollIntoView({ behavior: navigationScrollBehavior(), block: "start" });
  });
}

async function lookupAwards(event) {
  event.preventDefault();
  const apiKey = state.profileKey;
  if (!apiKey) {
    toast("请先在个人中心验证本人 API Key", "error");
    return;
  }
  const button = event.currentTarget.querySelector("button");
  button.disabled = true;
  setRequestStatus({ status: "running", title: "正在查询历史比赛", message: "正在核对已结束比赛的最终排名。" });
  try {
    const records = await api("/api/awards", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ api_key: apiKey }) });
    renderAwardResults(records, { resetPage: true });
    setRequestStatus({ status: "success", title: "历史比赛查询完成", message: records.length ? `共找到 ${records.length} 场历史比赛记录。` : "未查询到已结束比赛的参赛记录。" });
  } catch (error) {
    showRequestFailure(error, "历史比赛查询失败");
  } finally {
    button.disabled = false;
  }
}

async function initialize() {
  const bootstrapConfig = window.__C2P_APP_CONFIG__;
  delete window.__C2P_APP_CONFIG__;
  const applyApplicationConfig = (config) => {
    if (!config) return;
    if ($("#workbenchModeLink")) $("#workbenchModeLink").hidden = !config.workbench_enabled;
    state.demoData = shouldUseLocalDemoData(config, location.hostname);
  };
  const appConfigTask = bootstrapConfig
    ? Promise.resolve(bootstrapConfig)
    : api("/api/app-config").catch(() => null);
  if (bootstrapConfig) applyApplicationConfig(bootstrapConfig);
  else void appConfigTask.then(applyApplicationConfig);
  loadRollingAnnouncement();
  const savedKeysTask = new Promise((resolve) => {
    scheduleAfterInitialPaint(() => restoreSavedKeys().catch(() => undefined).finally(resolve), {
      timeoutMs: 1800,
      fallbackDelayMs: 400,
    });
  });
  try {
    state.contest = await api("/api/contest");
    state.leaderboardContestID = state.contest.id;
    state.submissionId = readBrowserStorage(`arena-submission-${state.contest.id}`);
    renderContest();
    scheduleAfterInitialPaint(() => runOptionalBrowserTask(async () => {
      applyApplicationConfig(await appConfigTask);
      let restored = await restoreLocalEntry();
      if (!restored && state.demoData) {
        const seeded = await seedLocalDemoEntries({ force: false, silent: true });
        if (seeded) restored = await restoreLocalEntry();
      }
      await savedKeysTask;
      const recovered = await recoverMissingContestSubmissions();
      if (recovered) await restoreLocalEntry();
      await Promise.all([
        autoLoadModelsForPurpose("image", { silent: true, reason: "restore" }),
        autoLoadModelsForPurpose("judge", { silent: true, reason: "restore" }),
      ]);
      updateComposerModelPicker();
      syncComposerControls();
      if (contestPhase() === "ended") await loadLeaderboard();
    }, "", 12000), { timeoutMs: 3200, fallbackDelayMs: 900 });
    updateComposerModelPicker();
  } catch (error) {
    renderContestLoadError();
    $("#contestTitle").textContent = "当前没有开放的图片赛";
    $("#contestPrompt").textContent = "管理员发布并激活比赛后，这里会显示统一生图提示词。";
    $("#phaseBadge").textContent = "暂无比赛";
    [...$("#entryForm").elements].forEach((element) => {
      if (updateGenerationSettingsControl(element)) return;
      if (isComposerPrepControl(element)) {
        element.disabled = element.dataset.loading === "true";
        return;
      }
      element.disabled = true;
    });
    if ($("#composerModelTrigger")) $("#composerModelTrigger").disabled = false;
    showRequestFailure(error, "比赛信息读取失败");
  }
}

function previewGeneratedImage() {
  if (!state.imageDataURL) {
    toast("生成图片后可预览原图");
    return;
  }
  openArchiveLightbox(state.imageDataURL, "本次生成的参赛作品原图");
}

$("#entryForm").addEventListener("submit", submitEntry);
$("#retryForm").addEventListener("submit", retryScore);
$("#reviewForm").addEventListener("submit", submitReview);
$("#awardLookupForm").addEventListener("submit", lookupAwards);
$("#refreshBoard").addEventListener("click", refreshLeaderboard);
$("#refreshArchive").addEventListener("click", refreshArchive);
$("#leaderboardContest").addEventListener("change", () => loadLeaderboard({ contestID: Number($("#leaderboardContest").value), resetPage: true }));
$("#localContestFilter")?.addEventListener("change", () => applyLocalFilters());
$("#localModelFilter")?.addEventListener("change", () => applyLocalFilters());
$("#resetLocalFilters")?.addEventListener("click", () => resetLocalFilters());
$("#deleteLocalSelected")?.addEventListener("click", () => handleDeleteLocalSelected());
$("#localSelectAll")?.addEventListener("change", () => {
  const checked = Boolean($("#localSelectAll")?.checked);
  document.querySelectorAll("#localSubmissionList [data-local-select]").forEach((input) => {
    input.checked = checked;
    toggleLocalSelection(input.dataset.localSelect, checked);
    input.closest(".local-submission-card")?.classList.toggle("is-selected", checked);
  });
  const pageEntries = [...document.querySelectorAll("#localSubmissionList [data-local-select]")].map((input) => ({ submission_id: input.dataset.localSelect }));
  updateLocalSelectionUI(pageEntries);
});
$("#closePlayerCareer").addEventListener("click", closePlayerCareer);
$("#playerCareerDialog").addEventListener("click", (event) => { if (event.target === event.currentTarget) closePlayerCareer(); });
$("#scoreDetailDialog").addEventListener("click", (event) => { if (event.target === event.currentTarget) event.currentTarget.close(); });
$("#loadImageModels").addEventListener("click", () => loadModels({
  keySelector: "#imageKey",
  selectSelector: "#imageModel",
  buttonSelector: "#loadImageModels",
  provider: state.imageProvider,
  purpose: "image",
}));
$("#loadJudgeModels").addEventListener("click", () => loadModels({
  keySelector: "#judgeKey",
  selectSelector: "#judgeModel",
  buttonSelector: "#loadJudgeModels",
  provider: "openai",
  purpose: "judge",
}));
$("#loadRetryJudgeModels").addEventListener("click", () => loadModels({
  keySelector: "#retryJudgeKey",
  selectSelector: "#retryJudgeModel",
  buttonSelector: "#loadRetryJudgeModels",
  provider: "openai",
  purpose: "judge",
}));
document.querySelectorAll('input[name="image_provider"]').forEach((input) => input.addEventListener("change", async () => {
  state.imageProvider = input.value;
  resetModelSelect("#imageModel", "切换接口后自动读取模型");
  try {
    await restoreSavedImageKey();
  } catch {
    toast("浏览器本地生图密钥读取失败", "error");
  }
  await autoLoadModelsForPurpose("image", { silent: true, reason: "provider" });
}));
$("#imageKey").addEventListener("input", () => {
  resetModelSelect("#imageModel", "Key 已变化，正在自动读取模型…");
  updateComposerModelPicker();
  scheduleAutoLoadModels("image");
});
$("#judgeKey").addEventListener("input", () => {
  resetModelSelect("#judgeModel", "Key 已变化，正在自动读取模型…");
  updateComposerModelPicker();
  scheduleAutoLoadModels("judge");
});
$("#imageKey").addEventListener("change", () => {
  window.clearTimeout(state.modelAutoLoadTimers.image);
  autoLoadModelsForPurpose("image", { silent: true, reason: "change" }).catch(() => {});
});
$("#judgeKey").addEventListener("change", () => {
  window.clearTimeout(state.modelAutoLoadTimers.judge);
  autoLoadModelsForPurpose("judge", { silent: true, reason: "change" }).catch(() => {});
});
$("#imageKey").addEventListener("blur", () => {
  if (keyLooksReady($("#imageKey").value) && $("#imageModel")?.dataset.ready !== "true") {
    autoLoadModelsForPurpose("image", { silent: true, reason: "blur" }).catch(() => {});
  }
});
$("#judgeKey").addEventListener("blur", () => {
  if (keyLooksReady($("#judgeKey").value) && $("#judgeModel")?.dataset.ready !== "true") {
    autoLoadModelsForPurpose("judge", { silent: true, reason: "blur" }).catch(() => {});
  }
});
$("#retryJudgeKey").addEventListener("input", () => resetModelSelect("#retryJudgeModel", "Key 已变化，请重新读取模型"));
$("#imageModel")?.addEventListener("change", () => writePreferredModel("image", $("#imageModel").value));
$("#judgeModel")?.addEventListener("change", () => writePreferredModel("judge", $("#judgeModel").value));
$("#promptAdjustment").addEventListener("input", updatePromptAdjustmentCounter);
$("#closeGenerationSettings").addEventListener("click", closeGenerationSettings);
$("#confirmGenerationSettings").addEventListener("click", () => {
  if (!generationSettingsReady()) {
    openGenerationSettings("缺少 Key 或模型。");
    toast("请补全生成设置");
    return;
  }
  closeGenerationSettings();
  updateComposerModelPicker();
  syncComposerControls();
  toast("生成设置已确认");
});
$("#composerModelTrigger")?.addEventListener("click", (event) => {
  event.preventDefault();
  event.stopPropagation();
  toggleComposerModelMenu();
});
$("#imageModel")?.addEventListener("change", updateComposerModelPicker);
$("#judgeModel")?.addEventListener("change", updateComposerModelPicker);
$("#imageKey")?.addEventListener("input", updateComposerModelPicker);
$("#judgeKey")?.addEventListener("input", updateComposerModelPicker);
document.addEventListener("click", (event) => {
  if (!state.composerModelMenuOpen) return;
  if (event.target.closest("#composerModelPicker")) return;
  closeComposerModelMenu();
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && state.composerModelMenuOpen) {
    event.preventDefault();
    closeComposerModelMenu();
  }
});
window.addEventListener("resize", () => {
  fitMobileHeroTitle();
  if (!state.composerModelMenuOpen) return;
  const active = document.querySelector(".composer-menu-item.is-open");
  if (active) positionComposerSubmenu(active);
});
$("#generationSettingsDialog").addEventListener("click", (event) => {
  if (event.target === event.currentTarget) closeGenerationSettings();
});
$("#copyPrompt").addEventListener("click", async () => {
  if (!state.contest) return;
  await navigator.clipboard.writeText(state.contest.prompt);
  toast("提示词已复制");
});
$("#downloadImage").addEventListener("click", () => {
  setSaveStatus();
  toast("已开始下载原图");
});
$("#previewGeneratedImage").addEventListener("click", previewGeneratedImage);
$("#previewGeneratedImageAction").addEventListener("click", previewGeneratedImage);
const canvasScoreTrigger = $("#canvasScoreTrigger");
const canvasScorePopover = $("#canvasScorePopover");
canvasScoreTrigger?.addEventListener("mouseenter", openCanvasScore);
canvasScoreTrigger?.addEventListener("mouseleave", scheduleCanvasScoreClose);
canvasScorePopover?.addEventListener("mouseenter", cancelCanvasScoreClose);
canvasScorePopover?.addEventListener("mouseleave", scheduleCanvasScoreClose);
canvasScoreTrigger?.addEventListener("click", (event) => {
  event.preventDefault();
  event.stopPropagation();
  openCanvasScore();
});
$("#closeCanvasScore")?.addEventListener("click", () => closeCanvasScore({ restoreFocus: true }));
window.addEventListener("resize", () => {
  positionCanvasScorePopover();
  if (isArchiveLightboxOpen()) renderImageViewer();
});
window.addEventListener("scroll", positionCanvasScorePopover, true);
document.addEventListener("click", (event) => {
  if (!isCanvasScoreOpen()) return;
  if (event.target.closest("#canvasScorePopover, #canvasScoreTrigger")) return;
  closeCanvasScore();
});
$("#clearSavedKeys").addEventListener("click", async () => {
  if (!await confirmAction({
    title: "清除本地密钥",
    message: "当前浏览器保存的生图、评分和个人资料 API Key 都会被删除，下次使用时需要重新填写。",
    confirmLabel: "确认清除",
  })) return;
  clearSavedCredentials().catch(() => toast("本地密钥清除失败", "error"));
});
$("#profileUnlockForm").addEventListener("submit", submitProfileUnlock);
$("#profileForm").addEventListener("submit", saveMyProfile);
document.querySelectorAll("[data-profile-stack-tab]").forEach((button) => button.addEventListener("click", () => {
  if (!button.disabled) switchProfileStack(button.dataset.profileStackTab);
}));
$("#profileSignature").addEventListener("input", updateProfilePreview);
$("#profileAvatarColor").addEventListener("input", () => {
  $("#profileRewardAvatar").value = "0";
  updateProfilePreview();
});
$("#profileRewardAvatar").addEventListener("change", updateProfilePreview);
document.querySelectorAll("[data-avatar-color]").forEach((button) => button.addEventListener("click", () => {
  $("#profileAvatarColor").value = button.dataset.avatarColor;
  $("#profileRewardAvatar").value = "0";
  updateProfilePreview();
}));
$("#profileBackgroundColor").addEventListener("input", () => {
  $("#profileRewardBackground").value = "0";
  updateProfilePreview();
});
$("#profileRewardBackground").addEventListener("change", updateProfilePreview);
document.querySelectorAll("[data-profile-color]").forEach((button) => button.addEventListener("click", () => {
  $("#profileBackgroundColor").value = button.dataset.profileColor;
  $("#profileRewardBackground").value = "0";
  updateProfilePreview();
}));
$("#reloadProfile").addEventListener("click", () => withRefreshState($("#reloadProfile"), () => fetchMyProfile(state.profileKey || $("#profileKey").value.trim())));
$("#downloadShareCard").addEventListener("click", () => {
  if (!state.shareCard) return;
  downloadShareCard(state.shareCard.blob, state.shareCard.entry);
  toast("分享卡片已下载，可发送给朋友");
});
$("#systemShareCard").addEventListener("click", systemShareCard);
$("#closeShareCard").addEventListener("click", closeShareCard);
$("#closeArchiveLightbox").addEventListener("click", closeArchiveLightbox);
$("#archiveLightbox").addEventListener("click", (event) => {
  if (event.target === event.currentTarget) closeArchiveLightbox();
});
$("#zoomOutArchiveImage")?.addEventListener("click", () => setImageViewerScale(imageViewer.scale / 1.25));
$("#zoomInArchiveImage")?.addEventListener("click", () => setImageViewerScale(imageViewer.scale * 1.25));
$("#fitArchiveImage")?.addEventListener("click", resetImageViewer);
$("#archivePreviewImage")?.addEventListener("load", renderImageViewer);
$("#archiveViewerStage")?.addEventListener("wheel", (event) => {
  event.preventDefault();
  setImageViewerScale(imageViewer.scale * (event.deltaY < 0 ? 1.12 : 1 / 1.12));
}, { passive: false });
$("#archiveViewerStage")?.addEventListener("dblclick", () => {
  if (imageViewer.scale > 1.01) resetImageViewer();
  else setImageViewerScale(2);
});
$("#archiveViewerStage")?.addEventListener("pointerdown", (event) => {
  if (imageViewer.scale <= 1.01 || event.button !== 0) return;
  imageViewer.dragging = true;
  imageViewer.pointerId = event.pointerId;
  imageViewer.startX = event.clientX;
  imageViewer.startY = event.clientY;
  imageViewer.originX = imageViewer.x;
  imageViewer.originY = imageViewer.y;
  event.currentTarget.setPointerCapture(event.pointerId);
  event.currentTarget.classList.add("is-dragging");
});
$("#archiveViewerStage")?.addEventListener("pointermove", (event) => {
  if (!imageViewer.dragging || event.pointerId !== imageViewer.pointerId) return;
  imageViewer.x = imageViewer.originX + event.clientX - imageViewer.startX;
  imageViewer.y = imageViewer.originY + event.clientY - imageViewer.startY;
  renderImageViewer();
});
function stopImageViewerDrag(event) {
  if (!imageViewer.dragging || event.pointerId !== imageViewer.pointerId) return;
  imageViewer.dragging = false;
  imageViewer.pointerId = null;
  event.currentTarget.classList.remove("is-dragging");
}
$("#archiveViewerStage")?.addEventListener("pointerup", stopImageViewerDrag);
$("#archiveViewerStage")?.addEventListener("pointercancel", stopImageViewerDrag);
$("#archiveLightbox").addEventListener("cancel", (event) => {
  event.preventDefault();
  closeArchiveLightbox();
});
$("#archiveLightbox").addEventListener("close", () => {
  $("#archivePreviewImage")?.removeAttribute("src");
  document.body.style.overflow = "";
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && isCanvasScoreOpen()) {
    event.preventDefault();
    closeCanvasScore({ restoreFocus: true });
    return;
  }
  if (event.key === "Escape" && isArchiveLightboxOpen()) {
    event.preventDefault();
    closeArchiveLightbox();
    return;
  }
  if (!isArchiveLightboxOpen()) return;
  if (event.key === "+" || event.key === "=") {
    event.preventDefault();
    setImageViewerScale(imageViewer.scale * 1.25);
  } else if (event.key === "-") {
    event.preventDefault();
    setImageViewerScale(imageViewer.scale / 1.25);
  } else if (event.key === "0") {
    event.preventDefault();
    resetImageViewer();
  }
});
$("#shareCardDialog").addEventListener("close", () => {
  if (state.shareCard?.objectURL) URL.revokeObjectURL(state.shareCard.objectURL);
  state.shareCard = null;
});
document.querySelectorAll("[data-view-tab]").forEach((button) => button.addEventListener("click", () => switchPublicView(button.dataset.viewTab)));
$("#workbenchModeLink")?.addEventListener("pointerenter", () => void prefetchWorkbench(), { once: true });
$("#workbenchModeLink")?.addEventListener("focus", () => void prefetchWorkbench(), { once: true });

initialize();
switchPublicView(location.hash.replace("#", "") || "contest");
